// ================================================================
//  biz-event-mapping-step  — Step 6 of the New Business Event wizard
//
//  Usage in parent template:
//    <biz-event-mapping-step
//        selected-operations="ctrl.selectedOperations"
//        selected-frame-type="ctrl.selectedFrameType"
//        mappings="ctrl.mappings">
//    </biz-event-mapping-step>
//
//  Bindings:
//    selectedOperations  < (one-way)  — operations configured in step 4
//    selectedFrameType   < (one-way)  — frame type chosen in step 5
//    mappings            = (two-way)  — wizard reads this array at submit
// ================================================================

angular.module('bizmetryApp')

    .directive('bizEventMappingStep', function () {
        return {
            restrict: 'E',
            templateUrl: 'profile/tabs/biz-events/mapping-step/biz-event-mapping-step.html',
            controller: 'BizEventMappingStepController',
            controllerAs: 'ctrl',
            bindToController: true,
            scope: {
                selectedOperations: '<',
                selectedFrameType: '<',
                templateFull: '<',
                preloadMappings: '<',
                mappings: '=',
                targetExpressions: '=',  // persisted on wizard so it survives back/forward nav
                bmelErrors: '=',         // true when any expression references a removed source
                onClearAll: '&'          // lets the wizard discard its preload source (IntelliSense data)
            }
        };
    })

    // ================================================================
    //  BizEventMappingStepController
    // ================================================================
    .controller('BizEventMappingStepController', function ($scope, $rootScope, $timeout, $mdDialog, $mdToast, BusinessEventsService, BmelEditorService, dialogService) {
        var ctrl = this;
        $scope.ctrl = ctrl;   // template uses ctrl.* — mirrors wizard convention

        function _showToast(message) {
            $mdToast.show(
                $mdToast.simple()
                    .textContent(message)
                    .position('top right')
                    .hideDelay(3500)
            );
        }

        // ── Payload sample helpers (duplicated from wizard — needed for tree building) ──

        function _getRequestSample(op) {
            return (op.payload && op.payload.request && op.payload.request.json_sample)
                ? op.payload.request.json_sample : null;
        }

        function _getOpResponseSample(op) {
            if (!op.payload || !op.payload.responses) { return null; }
            var responses = op.payload.responses;
            var preferred = ['200', '201', '202'];
            for (var i = 0; i < preferred.length; i++) {
                if (responses[preferred[i]] && responses[preferred[i]].json_sample) {
                    return responses[preferred[i]].json_sample;
                }
            }
            var keys = Object.keys(responses);
            for (var j = 0; j < keys.length; j++) {
                if (keys[j].charAt(0) === '2' && responses[keys[j]].json_sample) {
                    return responses[keys[j]].json_sample;
                }
            }
            return null;
        }

        ctrl.hasRequestSample = function (op) { return !!_getRequestSample(op); };
        ctrl.hasResponseSample = function (op) { return !!_getOpResponseSample(op); };

        // ── Frame type helpers ────────────────────────────────────────────
        ctrl.frameTypeName = function (ft) {
            return ft ? (ft.frame_name || ft.name || ft.frame_type_name || '(unnamed)') : '';
        };

        // Version of the template the target frame belongs to (pill next to the frame type)
        ctrl.templateVersion = function () {
            var t = ctrl.templateFull;
            return t ? (t.templateVersion || t.version || null) : null;
        };

        // ── Operation cardinality (ONE / MANY) ────────────────────────────
        //
        // A MANY operation can be invoked N times within the same transaction
        // (same correlation value), so every one of its sections is an ARRAY of
        // N occurrences:
        //   • BMEL refs carry "[]" after the section: {Op:Response Payload[]}.$.x
        //     — distinguishes an array of invocations from a real payload array.
        //   • Its scalar fields behave like array columns (column:<type>), so the
        //     same rules as array fields apply (flatten / aggregation functions).
        //   • The whole operation is itself a draggable array source (OPERATION,
        //     ref {Op:Invocations[]}.$) — the row source of a NESTED_TABLE with one
        //     row per invocation, whose columns pull from any section of the op.
        var _OPERATION_SECTION = 'Invocations';

        ctrl.isManyOp = function (entry) { return !!entry && entry.cardinality === 'MANY'; };

        function _opEntryOf(src) {
            return (src && src.opIdx !== undefined && ctrl.selectedOperations)
                ? ctrl.selectedOperations[src.opIdx] || null : null;
        }

        function _isManySource(src) {
            return !!src && (src.sourceType === 'OPERATION' || ctrl.isManyOp(_opEntryOf(src)));
        }

        function _opDisplayName(entry, opIdx) {
            return (entry && entry.op && (entry.op.operationName || entry.op.summary || entry.op.path)) || ('Op ' + opIdx);
        }

        // The whole MANY operation as an array source (null for ONE operations).
        ctrl.getOperationSource = function (opIdx, entry) {
            if (!ctrl.isManyOp(entry)) { return null; }
            var cacheKey = opIdx + '_OPERATION';
            if (!ctrl._simpleSourcesCache[cacheKey]) {
                ctrl._simpleSourcesCache[cacheKey] = _sourceField(
                    'op' + opIdx + '_OPERATION_$',          // == generic 'op{N}_{type}_{path}' formula
                    _opDisplayName(entry, opIdx) + ' []',
                    '$', 'OPERATION', 'array', entry, opIdx,
                    { depth: 0 }
                );
            }
            return ctrl._simpleSourcesCache[cacheKey];
        };

        // ── Context Variables catalogue ───────────────────────────────────
        var _CV_DEFS = {
            req_sys: [
                { base_id: 'CV_REQ_SIZE', label: 'Payload Size', icon: 'data_usage', desc: 'Request payload size in bytes', path: '$bm.request.payload_size', valueType: 'number' },
                { base_id: 'CV_REQ_TS', label: 'System Timestamp', icon: 'schedule', desc: 'Request timestamp (ISO-8601)', path: '$bm.request.timestamp', valueType: 'string' }
            ],
            req_token: [
                { base_id: 'CV_TOKEN_USERNAME', label: 'Username', icon: 'person', desc: 'Username from Bearer token', path: '$bm.token.username', valueType: 'string' },
                { base_id: 'CV_TOKEN_SUB', label: 'Subject (sub)', icon: 'fingerprint', desc: 'JWT sub claim', path: '$bm.token.sub', valueType: 'string' },
                { base_id: 'CV_TOKEN_ISS', label: 'Issuer (iss)', icon: 'business', desc: 'JWT iss claim', path: '$bm.token.iss', valueType: 'string' },
                { base_id: 'CV_TOKEN_EMAIL', label: 'Email', icon: 'email', desc: 'Email from Bearer token', path: '$bm.token.email', valueType: 'string' },
                { base_id: 'CV_TOKEN_ROLES', label: 'Roles / Groups', icon: 'group', desc: 'Roles/groups JWT claim', path: '$bm.token.roles', valueType: 'array' },
                { base_id: 'CV_TOKEN_EXP', label: 'Expiry (exp)', icon: 'timer_off', desc: 'JWT expiry timestamp', path: '$bm.token.exp', valueType: 'number' },
                { base_id: 'CV_TOKEN_IAT', label: 'Issued At (iat)', icon: 'timer', desc: 'JWT issued-at timestamp', path: '$bm.token.iat', valueType: 'number' }
            ],
            res_sys: [
                { base_id: 'CV_RES_SIZE', label: 'Payload Size', icon: 'data_usage', desc: 'Response payload size in bytes', path: '$bm.response.payload_size', valueType: 'number' },
                { base_id: 'CV_RES_TS', label: 'System Timestamp', icon: 'schedule', desc: 'Response timestamp (ISO-8601)', path: '$bm.response.timestamp', valueType: 'string' }
            ]
        };

        function _buildCtxVars(opIdx, group) {
            return _CV_DEFS[group].map(function (def) {
                return {
                    id: 'op' + opIdx + '_' + def.base_id,
                    label: def.label, icon: def.icon, desc: def.desc,
                    path: def.path, valueType: def.valueType, opIdx: opIdx
                };
            });
        }

        ctrl._emptyCtxVars = [];
        ctrl.getCtxVarsReqSys = function (opIdx) { return (ctrl._contextVarsCache || {})[opIdx + '_req_sys'] || ctrl._emptyCtxVars; };
        ctrl.getCtxVarsReqToken = function (opIdx) { return (ctrl._contextVarsCache || {})[opIdx + '_req_token'] || ctrl._emptyCtxVars; };
        ctrl.getCtxVarsResSys = function (opIdx) { return (ctrl._contextVarsCache || {})[opIdx + '_res_sys'] || ctrl._emptyCtxVars; };

        ctrl.contextToSource = function (cv) {
            return _sourceField(cv.id, cv.label, cv.path, 'CONTEXT', cv.valueType, null, cv.opIdx);
        };

        // ── Field type map (swagger → display type) ───────────────────────
        function _normalizePathForLookup(path) {
            return path.replace(/\[\d+\]/g, '');
        }

        function _buildFieldTypeMap(fields) {
            var map = {};
            // The schema flattener emits array<primitive> twice under the same path:
            // {$.llms, array} and {$.llms, string} — the latter is the ELEMENT type.
            // Type it as the CONTENT child so $.llms itself stays an array.
            var containerPaths = {};
            (fields || []).forEach(function (f) {
                if (f.path && (f.type === 'object' || f.type === 'array')) { containerPaths[f.path] = true; }
            });
            (fields || []).forEach(function (f) {
                if (!f.path || f.path === '$') { return; }
                if (f.type === 'object' || f.type === 'array') { return; }
                if (containerPaths[f.path]) {
                    f = angular.extend({}, f, { path: f.path + '.CONTENT' });
                }
                if (map[f.path]) { return; }
                var displayType;
                if (f.format) {
                    switch (f.format) {
                        case 'int64': displayType = 'int64'; break;
                        case 'int32': displayType = 'int32'; break;
                        case 'date-time': displayType = 'datetime'; break;
                        case 'date': displayType = 'date'; break;
                        case 'uuid': displayType = 'uuid'; break;
                        case 'binary': displayType = 'binary'; break;
                        case 'float': displayType = 'float'; break;
                        case 'double': displayType = 'double'; break;
                        default: displayType = f.format; break;
                    }
                } else {
                    displayType = f.type || 'string';
                }
                map[f.path] = displayType;
            });
            return map;
        }

        // Every array of its operation a payload source lies inside, outermost first, as
        // normalised paths — $.choices[0].message.tool_calls[0].id (tree) or
        // $.choices.message.tool_calls.id (IntelliSense / a saved mapping) →
        // ['$.choices', '$.choices.message.tool_calls']. Any number of levels.
        function _arrayAncestorPaths(src) {
            if (!src || (src.sourceType !== 'REQUEST_FIELD' && src.sourceType !== 'RESPONSE_FIELD')) { return []; }
            var path = src.path || '';
            var out = [];
            if (path.indexOf('[') > 0) {
                // Tree path: every [N] marks the array right before it.
                var re = /\[\d+\]/g, m;
                while ((m = re.exec(path)) !== null) {
                    out.push(path.substring(0, m.index).replace(/\[\d+\]/g, ''));
                }
                return out;
            }
            var entry = _opEntryOf(src);
            var op = entry && (entry.op || entry);
            if (!op) { return out; }
            var fields = src.sourceType === 'RESPONSE_FIELD'
                ? _getResponseFields(op)
                : ((op.payload && op.payload.request && op.payload.request.fields) || []);
            var arrays = {};
            fields.forEach(function (f) { if (f.type === 'array' && f.path) { arrays[f.path] = true; } });
            var segs = path.replace(/^\$\.?/, '').split('.');
            for (var i = 1; i < segs.length; i++) {
                var prefix = '$.' + segs.slice(0, i).join('.');
                if (arrays[prefix]) { out.push(prefix); }
            }
            return out;
        }

        function _isInsideArray(src) { return _arrayAncestorPaths(src).length > 0; }

        // The arrays a source lies inside, as sources of their own (same operation / section).
        function _arrayAncestorSources(src) {
            return _arrayAncestorPaths(src).map(function (arrPath) {
                return _sourceField('op' + src.opIdx + '_' + src.sourceType + '_' + arrPath, _lastPathSegment(arrPath),
                    arrPath, src.sourceType, 'array', src.opEntry, src.opIdx);
            });
        }

        // One element per array level, then the source's field on the innermost one:
        //   bmel:first({Op:Response Payload[]}.$.choices).message.content
        //   bmel:first(bmel:first({Op:Response Payload[]}.$.choices).message.tool_calls).id
        // and so on for any depth.
        function _firstElementExpression(src, rowScoped) {
            var arrays = _arrayAncestorPaths(src);
            if (!arrays.length) { return null; }
            var outer = _arrayAncestorSources(src)[0];
            var expr = 'bmel:first(' + _toBmelSource(outer, rowScoped).ref + ')';
            for (var i = 1; i < arrays.length; i++) {
                expr = 'bmel:first(' + expr + arrays[i].substring(arrays[i - 1].length) + ')';
            }
            var rest = (src.path || '').replace(/\[\d+\]/g, '').substring(arrays[arrays.length - 1].length);
            return expr + rest;
        }

        function _getResponseFields(op) {
            if (!op.payload || !op.payload.responses) { return []; }
            var responses = op.payload.responses;
            var preferred = ['200', '201', '202'];
            for (var i = 0; i < preferred.length; i++) {
                var r = responses[preferred[i]];
                if (r && r.fields && r.fields.length > 0) { return r.fields; }
            }
            var keys = Object.keys(responses);
            for (var j = 0; j < keys.length; j++) {
                if (keys[j].charAt(0) === '2' && responses[keys[j]].fields && responses[keys[j]].fields.length > 0) {
                    return responses[keys[j]].fields;
                }
            }
            return [];
        }

        // ── Tree builder ──────────────────────────────────────────────────
        function _buildTree(json, rootPath, depth, result, fieldTypeMap) {
            if (!json || typeof json !== 'object') { return; }
            var keys = Array.isArray(json) ? json.map(function (_, i) { return i; }) : Object.keys(json);
            keys.forEach(function (key) {
                var val = json[key];
                var path = Array.isArray(json)
                    ? rootPath + '[' + key + ']'
                    : (rootPath ? rootPath + '.' + key : key);
                var inferredType = val === null ? 'null' : Array.isArray(val) ? 'array' : typeof val;
                var swaggerType = fieldTypeMap ? fieldTypeMap[_normalizePathForLookup(path)] : null;
                var type = (inferredType === 'object' || inferredType === 'array')
                    ? inferredType : (swaggerType || inferredType);

                // Array of primitives (["gpt-4o", ...]): expose each element as an
                // OBJECT row with a CONTENT child holding the value, so nested-table
                // columns get a mappable child ($.llms[0].CONTENT) distinct from the
                // array itself ($.llms) — same shape as arrays of objects.
                if (Array.isArray(json) && inferredType !== 'object' && inferredType !== 'array') {
                    result.push({
                        path: path, label: String(key), valueType: 'object',
                        depth: depth, isLeaf: false, collapsed: depth > 1, hasChildren: true,
                        _children: [{
                            path: path + '.CONTENT', label: 'CONTENT', valueType: type,
                            depth: depth + 1, isLeaf: true, collapsed: false, hasChildren: false,
                            _children: []
                        }]
                    });
                    return;
                }

                var node = {
                    path: path, label: String(key), valueType: type,
                    depth: depth, isLeaf: (inferredType !== 'object' && inferredType !== 'array'),
                    collapsed: depth > 1, hasChildren: (inferredType === 'object' || inferredType === 'array'),
                    _children: []
                };
                result.push(node);
                if (!node.isLeaf) { _buildTree(val, path, depth + 1, node._children, fieldTypeMap); }
            });
        }

        function _flattenTree(nodes) {
            var result = [];
            nodes.forEach(function (node) {
                result.push(node);
                if (node.hasChildren && !node.collapsed && node._children.length) {
                    result = result.concat(_flattenTree(node._children));
                }
            });
            return result;
        }

        ctrl.getPayloadTree = function (entry, side) {
            var opIdx = ctrl.selectedOperations.indexOf(entry);
            var key = opIdx + '_' + side;
            if (!ctrl._treeCache[key]) {
                var raw = side === 'request' ? _getRequestSample(entry.op) : _getOpResponseSample(entry.op);
                var root = [];
                if (raw) {
                    var parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                    var fields = side === 'request'
                        ? (entry.op.payload && entry.op.payload.request && entry.op.payload.request.fields) || []
                        : _getResponseFields(entry.op);
                    _buildTree(parsed, '$', 0, root, _buildFieldTypeMap(fields));
                }
                ctrl._treeCache[key] = root;
            }
            if (!ctrl._flattenCache[key]) {
                ctrl._flattenCache[key] = _flattenTree(ctrl._treeCache[key]);
            }
            return ctrl._flattenCache[key];
        };

        ctrl.togglePayloadNode = function (entry, side, node) {
            node.collapsed = !node.collapsed;
            var opIdx = ctrl.selectedOperations.indexOf(entry);
            delete ctrl._flattenCache[opIdx + '_' + side];
            // Mapped fields inside the node appear / disappear: redraw their connectors.
            $timeout(ctrl.recalcConnectors, 30);
        };

        // ── Source field selection ────────────────────────────────────────
        ctrl.selectSourceField = function (field) {
            ctrl.selectedSourceField = (ctrl.selectedSourceField && ctrl.selectedSourceField.id === field.id)
                ? null : field;
        };

        ctrl.isSourceSelected = function (field) {
            return ctrl.selectedSourceField && ctrl.selectedSourceField.id === field.id;
        };

        // ── Wire drawing state ────────────────────────────────────────────
        ctrl._drawing = null;   // { field, x1, y1 } while drawing
        ctrl._liveWire = null;   // { d, x1, y1, x2, y2, snapped }
        ctrl._hoveredComp = null;   // component snapped during draw
        ctrl._connectors = [];     // [{id, d, x1, y1, x2, y2, cx, cy}] permanent
        ctrl._focusedConnId = null; // id of the connector currently under cursor

        var _bodyEl = null;

        function _getBodyEl() {
            if (!_bodyEl) { _bodyEl = document.querySelector('.cbew-mapping-body'); }
            return _bodyEl;
        }

        function _findEl(attr, value) {
            var body = _getBodyEl();
            if (!body) return null;
            var all = body.querySelectorAll('[' + attr + ']');
            for (var i = 0; i < all.length; i++) {
                if (all[i].getAttribute(attr) === String(value)) return all[i];
            }
            return null;
        }

        function _buildPath(x1, y1, x2, y2) {
            var cp = Math.max(Math.abs(x2 - x1) * 0.45, 50);
            return 'M ' + x1.toFixed(1) + ' ' + y1.toFixed(1)
                + ' C ' + (x1 + cp).toFixed(1) + ' ' + y1.toFixed(1)
                + ' ' + (x2 - cp).toFixed(1) + ' ' + y2.toFixed(1)
                + ' ' + x2.toFixed(1) + ' ' + y2.toFixed(1);
        }

        // Called on mousedown of any source field row
        // (alias kept in case any template still uses beginDrawing)
        ctrl.beginDrawing = ctrl.startConnect;

        // Recalculate permanent connector paths from current DOM positions
        ctrl.recalcConnectors = function () {
            var body = _getBodyEl();
            if (!body) { ctrl._connectors = []; return; }
            var bodyRect = body.getBoundingClientRect();
            ctrl._connectors = ctrl.mappings.map(function (m) {
                var srcEl = _findEl('data-field-id', m.source.id);
                var tgtEl = _findEl('data-comp-id', m.targetComponentId);
                if (!srcEl || !tgtEl) return null;
                var sr = srcEl.getBoundingClientRect();
                var tr = tgtEl.getBoundingClientRect();
                var x1 = sr.right - bodyRect.left;
                var y1 = sr.top + sr.height / 2 - bodyRect.top;
                var x2 = tr.left - bodyRect.left + 6;
                var y2 = tr.top + tr.height / 2 - bodyRect.top;
                return { id: m.source.id + '|' + m.targetComponentId, d: _buildPath(x1, y1, x2, y2), x1: x1, y1: y1, x2: x2, y2: y2, cx: (x1 + x2) / 2, cy: (y1 + y2) / 2 };
            }).filter(Boolean);
        };

        // ── Source field mapping state ────────────────────────────────────
        // Normaliza un source ID para comparación: elimina índices de array [N]
        // para que los IDs del IntelliSense ($.documents.documentType) hagan match
        // con los IDs del árbol DOM ($.documents[0].documentType).
        function _normId(id) { return (id || '').replace(/\[\d+\]/g, ''); }

        ctrl.isFieldMapped = function (fieldId) {
            var normFieldId = _normId(fieldId);
            return ctrl.mappings.some(function (m) {
                if (m.source.id === fieldId) return true;
                // También buscar en column sub-mappings del NESTED_TABLE,
                // normalizando ambos lados para cubrir el mismatch entre el
                // path plano del IntelliSense y el path indexado del árbol.
                if (m.columnMappings) {
                    var cols = Object.keys(m.columnMappings);
                    for (var i = 0; i < cols.length; i++) {
                        var cm = m.columnMappings[cols[i]];
                        if (cm && cm.source) {
                            if (cm.source.id === fieldId) return true;
                            if (_normId(cm.source.id) === normFieldId) return true;
                        }
                    }
                }
                return false;
            });
        };

        ctrl.removeMappingByField = function (fieldId, $event) {
            if ($event) { $event.stopPropagation(); }
            var normFieldId = _normId(fieldId);
            // Capture array mappings being removed so we can cascade to their children
            var removingArrays = ctrl.mappings.filter(function (m) {
                return m.source.id === fieldId && m.source.valueType === 'array';
            });
            // Capturar los targets afectados ANTES de filtrar
            var affectedCompIds = ctrl.mappings
                .filter(function (m) { return m.source.id === fieldId; })
                .map(function (m) { return m.targetComponentId; });
            // Eliminar mapeos directos
            ctrl.mappings = ctrl.mappings.filter(function (m) { return m.source.id !== fieldId; });
            // Cascade: remove column children of any array source just removed
            removingArrays.forEach(function (rm) {
                _removeArrayColumns(rm.targetComponentId, rm.source.id);
            });
            // Eliminar column sub-mappings que usen este source,
            // con normalización de path para cubrir el mismatch IntelliSense/árbol.
            ctrl.mappings.forEach(function (m) {
                if (m.columnMappings) {
                    Object.keys(m.columnMappings).forEach(function (colName) {
                        var cm = m.columnMappings[colName];
                        if (cm && cm.source) {
                            if (cm.source.id === fieldId || _normId(cm.source.id) === normFieldId) {
                                delete m.columnMappings[colName];
                            }
                        }
                    });
                }
            });
            // Limpiar expresiones BMEL (top-level + columnas) de cada target afectado sin sources
            affectedCompIds.forEach(function (cid) {
                var stillHasSources = ctrl.mappings.some(function (m) { return m.targetComponentId === cid; });
                if (!stillHasSources) {
                    _clearExpressionsForComp(cid);
                }
            });
            _validateAllExpressions();
            $timeout(ctrl.recalcConnectors, 50);
        };

        // Helper for tree nodes — computes stable field ID without ng-repeat scope tricks
        ctrl.treeFieldId = function (entry, side, path) {
            var opIdx = ctrl.selectedOperations.indexOf(entry);
            return 'op' + opIdx + '_' + (side === 'request' ? 'REQUEST_FIELD' : 'RESPONSE_FIELD') + '_' + path;
        };

        // ── Hierarchical unlink (section / group level) ───────────────────

        // Operation section: any mapping from this opIdx
        ctrl.isOpMapped = function (opIdx) {
            return ctrl.mappings.some(function (m) { return m.source.opIdx === opIdx; });
        };
        ctrl.unlinkOp = function (opIdx, $event) {
            if ($event) { $event.stopPropagation(); }
            ctrl.mappings = ctrl.mappings.filter(function (m) { return m.source.opIdx !== opIdx; });
            $timeout(ctrl.recalcConnectors, 50);
        };

        // Generic group by sourceType
        ctrl.isGroupMapped = function (opIdx, sourceType) {
            return ctrl.mappings.some(function (m) {
                return m.source.opIdx === opIdx && m.source.sourceType === sourceType;
            });
        };
        ctrl.unlinkGroup = function (opIdx, sourceType, $event) {
            if ($event) { $event.stopPropagation(); }
            ctrl.mappings = ctrl.mappings.filter(function (m) {
                return !(m.source.opIdx === opIdx && m.source.sourceType === sourceType);
            });
            $timeout(ctrl.recalcConnectors, 50);
        };

        // Request context group: req_sys ($bm.request.*) + token ($bm.token.*)
        ctrl.isReqCtxMapped = function (opIdx) {
            return ctrl.mappings.some(function (m) {
                return m.source.opIdx === opIdx && m.source.sourceType === 'CONTEXT'
                    && m.source.path.indexOf('$bm.response') !== 0;
            });
        };
        ctrl.unlinkReqCtx = function (opIdx, $event) {
            if ($event) { $event.stopPropagation(); }
            ctrl.mappings = ctrl.mappings.filter(function (m) {
                return !(m.source.opIdx === opIdx && m.source.sourceType === 'CONTEXT'
                    && m.source.path.indexOf('$bm.response') !== 0);
            });
            $timeout(ctrl.recalcConnectors, 50);
        };

        // Response context group: res_sys ($bm.response.*)
        ctrl.isResCtxMapped = function (opIdx) {
            return ctrl.mappings.some(function (m) {
                return m.source.opIdx === opIdx && m.source.sourceType === 'CONTEXT'
                    && m.source.path.indexOf('$bm.response') === 0;
            });
        };
        ctrl.unlinkResCtx = function (opIdx, $event) {
            if ($event) { $event.stopPropagation(); }
            ctrl.mappings = ctrl.mappings.filter(function (m) {
                return !(m.source.opIdx === opIdx && m.source.sourceType === 'CONTEXT'
                    && m.source.path.indexOf('$bm.response') === 0);
            });
            $timeout(ctrl.recalcConnectors, 50);
        };

        // ── Header actions ───────────────────────────────────────────────

        ctrl.clearAllMappings = function () {
            ctrl.mappings          = [];
            ctrl.targetExpressions = {};
            ctrl.bmelErrors        = false;
            ctrl._bmelErrorMap     = {};

            // preloadMappings is one-way ('<'), so clearing ctrl.mappings here
            // does NOT reach the wizard's source data (ctrl._intelliSenseMappings).
            // Without this, Back → Next re-creates the directive, the preload
            // watch fires again on the still-intact wizard data, and the
            // mappings we just cleared silently reappear.
            if (ctrl.onClearAll) { ctrl.onClearAll(); }

            $timeout(ctrl.recalcConnectors, 50);
        };

        // ── BMEL expression validation ─────────────────────────────────────
        //
        // After any source removal, expressions that referenced the removed
        // field become invalid (the ref no longer appears in availableSources).
        // _validateAllExpressions() rescans every entry in targetExpressions
        // and sets ctrl.bmelErrors so canAdvance() can block the Create button.
        //
        // Column-scoped keys: "<compId>|col|<colName>"
        // Top-level keys:     "<compId>"
        //
        function _validateAllExpressions() {
            var hasError = false;
            var errorMap = {};

            // Build a fast lookup: compId → component object, for type resolution
            var compLookup = {};
            (ctrl.getSortedComponents ? ctrl.getSortedComponents() : []).forEach(function (c) {
                var id = String(c.frame_component_id || c.frame_field_id || c.component_id || c.id || '');
                if (id) { compLookup[id] = c; }
            });

            Object.keys(ctrl.targetExpressions || {}).forEach(function (key) {
                var expr = ctrl.targetExpressions[key];
                if (!expr) { return; }

                var parts   = key.split('|col|');
                var compId  = parts[0];
                var colName = parts[1] || null;   // set for NESTED_TABLE column keys

                var sources = _buildAllSourcesForBmel(compId);
                if (!sources || !sources.length) {
                    hasError      = true;
                    errorMap[key] = true;
                    return;
                }

                // Resolve expectedBmelType so type-mismatch errors (e.g. STRING→NUMBER)
                // are caught here — not only when the BMEL editor is opened manually.
                var expectedBmelType  = null;
                var expectedTypeLabel = null;
                var comp = compLookup[compId];
                if (comp) {
                    if (colName) {
                        // NESTED_TABLE column key — find column in nested_table_columns
                        var ntCols = comp.nested_table_columns || [];
                        var colDef = null;
                        for (var ci = 0; ci < ntCols.length; ci++) {
                            var ntc = ntCols[ci];
                            var ntcName = ntc.name || ntc.column_name || ntc.frame_field_name || '';
                            if (ntcName === colName) { colDef = ntc; break; }
                        }
                        if (colDef) {
                            var eiCol = _getExpectedBmelInfoForCol(colDef);
                            expectedBmelType  = eiCol.type;
                            expectedTypeLabel = eiCol.label;
                        }
                    } else {
                        if (_getExpectedBmelInfo) {
                            var ei = _getExpectedBmelInfo(comp);
                            expectedBmelType  = ei.type;
                            expectedTypeLabel = ei.label;
                        }
                    }
                }

                var errors = BmelEditorService.validateExpression(
                    expr, sources, expectedBmelType, expectedTypeLabel, _bmelResourceTypes()
                );
                // Warnings (e.g. a renamed function's old name) don't block the mapping.
                if ((errors || []).some(function (e) { return e.severity !== 'warning'; })) {
                    hasError      = true;
                    errorMap[key] = true;
                }
            });

            ctrl._bmelErrorMap = errorMap;
            ctrl.bmelErrors    = hasError;
        }

        // Returns true when the top-level expression for a component is invalid,
        // OR when any of its own NESTED_TABLE columns has an invalid expression —
        // a column error must surface on the parent row too, otherwise it's only
        // visible once you expand that specific table, and Apply Changes staying
        // disabled looks unexplained everywhere else in the mapper.
        ctrl.hasBmelExpressionError = function (comp) {
            var compId = comp.frame_component_id || comp.frame_field_id || comp.component_id || comp.id;
            if (!ctrl._bmelErrorMap) { return false; }
            if (ctrl._bmelErrorMap[compId]) { return true; }
            var prefix = compId + '|col|';
            return Object.keys(ctrl._bmelErrorMap).some(function (k) { return k.indexOf(prefix) === 0; });
        };

        // Returns true when a column-scoped expression is invalid.
        // A column's expression when it's more than the plain ref of its source
        // (e.g. bmel:first(...)) — shown under the column instead of the bare ref.
        ctrl.getColExpression = function (comp, colName) {
            var compId = comp.frame_component_id || comp.id;
            var expr = (ctrl.targetExpressions || {})[compId + '|col|' + colName];
            var cm = ctrl.getColMapping(comp, colName);
            if (!expr || !cm || !cm.source) { return null; }
            var rowScoped = _isInvocationTable(compId);
            return expr.trim() === _toBmelSource(cm.source, rowScoped).ref ? null : expr;
        };

        ctrl.hasColBmelError = function (comp, colName) {
            var compId = comp.frame_component_id || comp.frame_field_id || comp.component_id || comp.id;
            var key    = compId + '|col|' + colName;
            return !!(ctrl._bmelErrorMap && ctrl._bmelErrorMap[key]);
        };

        ctrl.runIntelliSense = function () {
            if (ctrl.intelliSenseRunning) { return; }
            if (!ctrl.selectedFrameType || !ctrl.templateFull) {
                _showToast('⚠️ Template or frame type not loaded yet.');
                return;
            }

            ctrl.intelliSenseRunning = true;

            // ── Build request payload ─────────────────────────────────────────
            var operations = (ctrl.selectedOperations || []).map(function (entry) {
                var op = entry.op || entry;
                return {
                    operation_id: op.operationId || op.operation_id || op.id,
                    operation_name: op.operationName || op.operation_name || op.summary || op.path,
                    method: op.method || op.api_method,
                    path: op.path || op.api_endpoint,
                    summary: op.summary,
                    payload: op.payload || null,
                    // ONE / MANY — IntelliSense models a MANY operation as an array
                    // of invocations ({Op:Section[]} refs, per-invocation tables)
                    cardinality: entry.cardinality || 'ONE'
                };
            });

            var requestPayload = {
                operations: operations,
                template: ctrl.templateFull,
                frame_type_id: ctrl.selectedFrameType.frame_type_id
            };

            // The composer always maps with the agent (BMEL-aware, ~30-60 s);
            // the deterministic engine only picks operations in the wizards.
            var engine = BusinessEventsService.INTELLISENSE_ENGINE.AGENT;

            // ── Polling state ─────────────────────────────────────────────────
            var _cancelled = false;
            var _jobId       = null;   // known once /start answers — needed to cancel the agent
            var _pollTimeout = null;

            function _stopPoll() {
                if (_pollTimeout) { $timeout.cancel(_pollTimeout); _pollTimeout = null; }
                ctrl.intelliSenseRunning = false;
            }

            // ── Progress dialog ───────────────────────────────────────────────
            // Uses a separate broadcast channel ('intellisense:mapping:progress')
            // so it doesn't interfere with the wizard's operation-selection dialog.
            $mdDialog.show({
                multiple: true,
                clickOutsideToClose: false,
                escapeToClose: false,
                template: _IS_MAPPING_PROGRESS_TEMPLATE,
                controller: function ($scope, $mdDialog) {
                    $scope.percent = 0;
                    $scope.stage = 'Starting…';

                    $scope.$on('intellisense:mapping:progress', function (e, data) {
                        $scope.percent = data.percent || 0;
                        $scope.stage = data.message || '';
                    });

                    $scope.cancelIntelliSense = function () {
                        _cancelled = true;
                        _stopPoll();
                        BusinessEventsService.cancelIntelliSense(_jobId, engine);   // stop the agent server-side
                        $mdDialog.hide('cancelled');
                    };
                }
            });

            // ── Step 1: start async job ───────────────────────────────────────
            BusinessEventsService.startIntelliSense(requestPayload, engine)
                .then(function (response) {
                    _jobId = response.data && response.data.job_id;
                    if (_cancelled) { BusinessEventsService.cancelIntelliSense(_jobId, engine); return; }
                    var jobId = response.data && response.data.job_id;
                    if (!jobId) { throw new Error('No job_id returned by server'); }
                    _schedulePoll(jobId);
                })
                .catch(function (err) {
                    if (_cancelled) { return; }
                    _stopPoll();
                    $mdDialog.hide();
                    var msg = (err.data && (err.data.detail || err.data.message)) || 'Failed to start IntelliSense.';
                    _showToast('❌ ' + msg);
                });

            // ── Step 2: chained polling — one request in flight at a time ─────
            function _schedulePoll(jobId) {
                if (_cancelled) { return; }
                _pollTimeout = $timeout(function () {

                    BusinessEventsService.pollIntelliSenseStatus(jobId, engine)
                        .then(function (res) {
                            if (_cancelled) { return; }
                            var data = res.data;

                            $rootScope.$broadcast('intellisense:mapping:progress', {
                                percent: data.percent || 0,
                                message: data.message || ''
                            });

                            if (data.status === 'COMPLETE') {
                                _stopPoll();

                                // Flash 100% before closing
                                $rootScope.$broadcast('intellisense:mapping:progress', {
                                    percent: 100,
                                    message: 'Done'
                                });

                                $timeout(function () {
                                    $mdDialog.hide();

                                    var result = data.result || {};
                                    var suggested = _translateIntelliSenseMappings(result.mappings || []);

                                    if (suggested.length === 0) {
                                        _showToast('🤔 IntelliSense could not find confident mappings.');
                                        return;
                                    }

                                    // Components IntelliSense mapped that already have a mapping:
                                    // ask whether its proposal overrides them (Yes) or the
                                    // existing configuration stays and it only maps the rest (No).
                                    var mappedIds = {};
                                    ctrl.mappings.forEach(function (existing) { mappedIds[existing.targetComponentId] = true; });
                                    var overlap = [];
                                    suggested.forEach(function (m) {
                                        if (mappedIds[m.targetComponentId] && overlap.indexOf(m.targetComponentId) < 0) {
                                            overlap.push(m.targetComponentId);
                                        }
                                    });
                                    if (!overlap.length) {
                                        _applyIntelliSense(suggested, [], result.summary);
                                        return;
                                    }
                                    var names = overlap.map(function (id) {
                                        var m = suggested.find(function (x) { return x.targetComponentId === id; });
                                        return (m && m.targetComponentName) || id;
                                    });
                                    dialogService.showDecisionDialog(
                                        'Override existing mappings?',
                                        'IntelliSense mapped <strong>' + overlap.length + '</strong> component' +
                                        (overlap.length === 1 ? '' : 's') + ' that already ' + (overlap.length === 1 ? 'has' : 'have') +
                                        ' a mapping (' + names.slice(0, 6).join(', ') + (names.length > 6 ? ', …' : '') + ').<br><br>' +
                                        '<strong>Yes</strong> overrides them with IntelliSense\'s mapping and BMEL expression.<br>' +
                                        '<strong>No</strong> keeps the existing configuration and only maps the components that have none.',
                                        { goBack: 'No', proceed: 'Yes' }
                                    ).then(function (decision) {
                                        _applyIntelliSense(suggested, decision === 'proceed' ? overlap : null, result.summary);
                                    });

                                }, 400);

                            } else if (data.status === 'ERROR') {
                                _stopPoll();
                                $mdDialog.hide();
                                _showToast('❌ ' + (data.message || 'IntelliSense processing failed.'));

                            } else {
                                // still running — schedule next poll
                                _schedulePoll(jobId);
                            }
                        })
                        .catch(function () {
                            if (_cancelled) { return; }
                            _stopPoll();
                            $mdDialog.hide();
                            _showToast('❌ Lost connection to IntelliSense service.');
                        });

                }, BusinessEventsService.intelliSensePollInterval(engine));
            }
        };

        // Applies IntelliSense's suggestions to the composer.
        //   overrideIds = [componentIds] — those components drop their current mappings and
        //                 BMEL (comp + its columns) and take IntelliSense's;
        //   overrideIds = null           — components that already have a mapping keep it
        //                 untouched; only the unmapped ones are filled.
        function _applyIntelliSense(suggested, overrideIds, summary) {
            var override = {};
            (overrideIds || []).forEach(function (id) { override[id] = true; });

            if (overrideIds && overrideIds.length) {
                for (var k = ctrl.mappings.length - 1; k >= 0; k--) {
                    if (override[ctrl.mappings[k].targetComponentId]) { ctrl.mappings.splice(k, 1); }
                }
                Object.keys(ctrl.targetExpressions || {}).forEach(function (key) {
                    var compId = key.split('|col|')[0];
                    if (override[compId]) { delete ctrl.targetExpressions[key]; }
                });
            }

            var keep = {};
            if (!overrideIds) {
                ctrl.mappings.forEach(function (existing) { keep[existing.targetComponentId] = true; });
            }

            var added = 0;
            suggested.forEach(function (m) {
                if (keep[m.targetComponentId]) { return; }   // existing configuration stays
                var alreadyMapped = ctrl.mappings.some(function (existing) {
                    return existing.targetComponentId === m.targetComponentId
                        && existing.source.id === m.source.id;
                });
                if (alreadyMapped) { return; }
                ctrl.mappings.push(m);
                added++;

                // BMEL del componente y de sus columnas (regla e) — never over a manual edit
                if (m.bmelExpression && !ctrl.targetExpressions[m.targetComponentId]) {
                    ctrl.targetExpressions[m.targetComponentId] = m.bmelExpression;
                }
                if (m.columnMappings) {
                    Object.keys(m.columnMappings).forEach(function (colName) {
                        var cmEntry = m.columnMappings[colName];
                        if (cmEntry && cmEntry.bmelExpression) {
                            var exprKey = m.targetComponentId + '|col|' + colName;
                            if (!ctrl.targetExpressions[exprKey]) {
                                ctrl.targetExpressions[exprKey] = cmEntry.bmelExpression;
                            }
                        }
                    });
                }
            });

            // IntelliSense doesn't know about cardinality —
            // fix up its refs / impossible suggestions.
            _reconcileCardinality();
            _validateAllExpressions();

            $timeout(ctrl.recalcConnectors, 50);

            summary = summary || {};
            var overridden = (overrideIds || []).length;
            _showToast('✨ IntelliSense mapped ' + added + ' field(s)' +
                (overridden ? ', overriding ' + overridden + ' component(s)' : '') +
                (summary.mapped ? ' (' + summary.mapped + '/' + summary.total_frame_components + ' components)' : '') + '.');
        }

        // ── IntelliSense progress dialog template ─────────────────────────────
        // Same visual design as the wizard's operation-selection progress dialog.
        var _IS_MAPPING_PROGRESS_TEMPLATE = [
            '<md-dialog class="cbew-is-progress-dialog" aria-label="IntelliSense in progress">',

            '  <md-toolbar style="background:linear-gradient(135deg,#667eea 0%,#764ba2 100%);',
            '               min-height:64px; flex-shrink:0;">',
            '    <div class="md-toolbar-tools">',
            '      <md-icon style="color:white; margin-right:10px; flex-shrink:0;">auto_awesome</md-icon>',
            '      <h2 style="color:white; font-weight:500; font-size:18px; margin:0;',
            '                 flex:1; text-align:left;">IntelliSense in Progress...</h2>',
            '    </div>',
            '  </md-toolbar>',

            '  <md-dialog-content style="padding:24px 28px 20px; background:#fff; min-width:420px;">',

            '    <div style="display:flex; align-items:center; gap:12px; margin-bottom:16px;">',
            '      <md-progress-circular md-mode="indeterminate" md-diameter="22"',
            '          style="flex-shrink:0;"></md-progress-circular>',
            '      <span style="font-size:13px; color:#546e7a; flex:1;">{{ stage || "Starting..." }}</span>',
            '      <span style="font-size:13px; font-weight:700; color:#764ba2;',
            '                   min-width:36px; text-align:right;">{{ percent }}%</span>',
            '    </div>',

            '    <div style="height:8px; background:#ede7f6; border-radius:4px; overflow:hidden;">',
            '      <div style="height:100%; border-radius:4px;',
            '                  background:linear-gradient(90deg,#667eea 0%,#764ba2 100%);',
            '                  transition:width 0.4s ease;"',
            '           ng-style="{width: (percent || 0) + \'%\'}">',
            '      </div>',
            '    </div>',

            '    <p style="margin:10px 0 0 0; font-size:12px; color:#b0bec5; text-align:right;">',
            '      You can cancel at any time.',
            '    </p>',
            '  </md-dialog-content>',

            '  <div style="display:flex; justify-content:flex-end; padding:10px 16px;',
            '              background:#fafafa; border-top:1px solid #eeeeee;">',
            '    <button type="button" ng-click="cancelIntelliSense()"',
            '            style="background:#f5f5f5; color:#666; border:1px solid #ddd;',
            '                   border-radius:4px; font-size:13px; font-weight:500;',
            '                   height:34px; padding:0 20px; cursor:pointer; font-family:inherit;">',
            '      Cancel',
            '    </button>',
            '  </div>',
            '</md-dialog>'
        ].join('\n');

        // ── Translate IntelliSense API response → ctrl.mappings format ───────
        //
        // CRITICAL: sourceId must match the DOM data-field-id rendered in the
        // template so that recalcConnectors() can find the element and draw the
        // SVG connector line.  The format mirrors what the HTML hardcodes:
        //   op{N}_RESPONSE_FIELD_{$.path}
        //   op{N}_REQUEST_FIELD_{$.path}
        //   op{N}_URI_PARAM_{name}
        //   op{N}_HEADER_{name}
        //   op{N}_QUERY_PARAM_{name}
        var _BACKEND_TO_FRONTEND_TYPE = {
            // Valores actuales del backend (SourceType enum — uppercase)
            'RESPONSE_FIELD':    'RESPONSE_FIELD',
            'REQUEST_FIELD':     'REQUEST_FIELD',
            'URI_PARAM':         'URI_PARAM',
            'QUERY_PARAM':       'QUERY_PARAM',
            'REQUEST_HEADER':    'HEADER',          // backend REQUEST_HEADER → frontend HEADER
            // Context variables — backend now uses two distinct values
            'REQUEST_CONTEXT':   'CONTEXT',          // $bm.request.*, $bm.token.*, correlation
            'RESPONSE_CONTEXT':  'CONTEXT',          // $bm.response.*
            // Legacy: old records persisted as 'CONTEXT' before the split
            'CONTEXT':           'CONTEXT',
            // Whole MANY-cardinality operation as an array of its invocations
            'OPERATION':         'OPERATION',
            // Aliases legacy (lowercase) por compatibilidad
            'response_payload':  'RESPONSE_FIELD',
            'request_payload':   'REQUEST_FIELD',
            'http_header':       'HEADER',
            'uri_param':         'URI_PARAM',
            'query_param':       'QUERY_PARAM'
        };

        // ── Type lookup from payload tree cache ──────────────────────────────────
        // Used by _translateIntelliSenseMappings as a reliable fallback when
        // _buildFieldTypeMap(rawFields) returns an empty map or is missing entries
        // (e.g. when the operation's parsed field definitions are incomplete or the
        // response has no fields[] populated).
        //
        // Searches the tree that was already built when the user opened the
        // Response/Request Payload section — the SAME source that manual drag-and-
        // drop uses via treeNodeToSource() — so IntelliSense type resolution is
        // always consistent with what the user sees when mapping manually.
        //
        // The tree is built on demand if not yet cached, so it works even when
        // IntelliSense is run before the user manually expands the payload section.
        function _typeFromTree(opEntry, opIdx, isResp, fieldPath) {
            if (!opEntry || !fieldPath) { return null; }
            var side     = isResp ? 'response' : 'request';
            var cacheKey = opIdx + '_' + side;

            // Build tree directly using opIdx as cache key — avoids indexOf()
            // ambiguity in getPayloadTree() that causes number fields to show as string.
            if (!ctrl._treeCache[cacheKey]) {
                var _ftOp  = opEntry.op || opEntry;
                var _ftRaw = isResp ? _getOpResponseSample(_ftOp) : _getRequestSample(_ftOp);
                if (_ftRaw) {
                    var _ftParsed = (typeof _ftRaw === 'string') ? JSON.parse(_ftRaw) : _ftRaw;
                    var _ftFields = isResp
                        ? _getResponseFields(_ftOp)
                        : ((_ftOp.payload && _ftOp.payload.request && _ftOp.payload.request.fields) || []);
                    var _ftRoot = [];
                    _buildTree(_ftParsed, '$', 0, _ftRoot, _buildFieldTypeMap(_ftFields));
                    ctrl._treeCache[cacheKey] = _ftRoot;
                    delete ctrl._flattenCache[cacheKey];
                }
            }
            var tree = ctrl._treeCache[cacheKey];
            if (!tree || !tree.length) { return null; }

            var normTarget = fieldPath.replace(/\[\d+\]/g, '');

            function _search(nodes) {
                for (var i = 0; i < nodes.length; i++) {
                    var n     = nodes[i];
                    var nNorm = (n.path || '').replace(/\[\d+\]/g, '');
                    if (nNorm === normTarget || n.path === fieldPath) {
                        // Skip 'object' — these are the [0] index-wrapper nodes from
                        // JSON sample parsing and are not valid source valueTypes.
                        // 'array' IS valid (e.g. $.accounts mapped to NESTED_TABLE).
                        var t = n.valueType;
                        return (t && t !== 'object') ? t : null;
                    }
                    if (n._children && n._children.length) {
                        var found = _search(n._children);
                        if (found !== null) { return found; }
                    }
                }
                return null;
            }

            return _search(tree);
        }


        // ── Context var path → DOM base_id lookup ──────────────────────────────
        // Mirrors _CV_DEFS. Used by _resolveContextSourceId to map a persisted
        // $bm.* path back to the correct DOM data-field-id.
        var _CV_PATH_TO_BASE_ID = {
            '$bm.request.payload_size':  'CV_REQ_SIZE',
            '$bm.request.timestamp':     'CV_REQ_TS',
            '$bm.token.username':        'CV_TOKEN_USERNAME',
            '$bm.token.sub':             'CV_TOKEN_SUB',
            '$bm.token.iss':             'CV_TOKEN_ISS',
            '$bm.token.email':           'CV_TOKEN_EMAIL',
            '$bm.token.roles':           'CV_TOKEN_ROLES',
            '$bm.token.exp':             'CV_TOKEN_EXP',
            '$bm.token.iat':             'CV_TOKEN_IAT',
            '$bm.response.payload_size': 'CV_RES_SIZE',
            '$bm.response.timestamp':    'CV_RES_TS'
        };

        // Resolves the DOM data-field-id for a CONTEXT source coming from the backend.
        // Returns the correct 'op{idx}_{baseId}' or 'op{idx}_CORR_ID' string,
        // or falls back to the generic formula when the path is unrecognized.
        function _resolveContextSourceId(opIdx, opEntry, fieldPath, backendType) {
            // 1. Known $bm.* platform var
            var baseId = _CV_PATH_TO_BASE_ID[fieldPath];
            if (baseId) {
                return 'op' + opIdx + '_' + baseId;
            }
            // 2. Correlation key field — path matches the op's correlationKey.sourcePath
            if (opEntry) {
                var ck = opEntry.correlationKey;
                if (ck && ck.sourcePath && ck.sourcePath === fieldPath) {
                    return 'op' + opIdx + '_CORR_ID';
                }
            }
            // 3. Fallback — unknown CONTEXT path, use generic formula
            return 'op' + opIdx + '_CONTEXT_' + fieldPath;
        }

        // field_name is null in some backend responses — derive a clean
        // human-readable label from the last segment of field_path instead of
        // falling back to the full path string (e.g. 'riskScore' not '$.riskScore').
        function _lastPathSegment(p) {
            if (!p) { return p; }
            return p.replace(/\[\d+\]/g, '').split('.').filter(Boolean).pop() || p;
        }

        var _CV_TYPE_MAP = {
            '$bm.request.payload_size':  'number',
            '$bm.request.timestamp':     'string',
            '$bm.token.username':        'string',
            '$bm.token.sub':             'string',
            '$bm.token.iss':             'string',
            '$bm.token.email':           'string',
            '$bm.token.roles':           'array',
            '$bm.token.exp':             'number',
            '$bm.token.iat':             'number',
            '$bm.response.payload_size': 'number',
            '$bm.response.timestamp':    'string'
        };

        // _translateSource — builds one directive source object (DOM-matching id,
        // resolved valueType, BMEL overrides) from a backend source_type + path.
        // Shared by component sources and NESTED_TABLE column sources: a column of
        // a MANY-operation table can come from any section of that operation, not
        // just the row array's own section, so each column resolves on its own.
        function _translateSource(opEntry, opIdx, backendType, fieldPath, fieldName, extra) {
            // Map backend source_type → frontend sourceType string
            var frontendType = _BACKEND_TO_FRONTEND_TYPE[backendType] || backendType;
            var fieldKey     = fieldPath || fieldName || '';

            // Build sourceId matching the DOM data-field-id exactly.
            //
            // CONTEXT sources have a special DOM id scheme:
            //   $bm.* platform vars  → 'op{idx}_{CV_BASE_ID}'  e.g. 'op0_CV_REQ_SIZE'
            //   correlation key      → 'op{idx}_CORR_ID'
            // These do NOT follow the generic 'op{idx}_{type}_{path}' formula.
            var sourceId = (frontendType === 'CONTEXT')
                ? _resolveContextSourceId(opIdx, opEntry, fieldKey, backendType)
                : 'op' + opIdx + '_' + frontendType + '_' + fieldKey;

            var fieldLabel = fieldName || _lastPathSegment(fieldPath) || fieldPath;

            // ── Resolve actual valueType from the operation's field definitions ──
            var valueType = 'string';
            if (frontendType === 'OPERATION') {
                // The whole MANY operation — an array of its N invocations.
                valueType  = 'array';
                fieldLabel = _opDisplayName(opEntry, opIdx) + ' []';
            } else if (frontendType === 'CONTEXT') {
                // $bm.* platform vars and CORR_ID fields are NOT in JSON payload trees.
                // Looking them up via typeMap or _typeFromTree always returns null
                // and falls back to 'string'. Resolve from the static type catalog
                // that mirrors _CV_DEFS, guaranteeing consistency between create/edit.
                valueType = sourceId.indexOf('_CORR_ID') >= 0
                    ? 'string'   // correlation key is always a string
                    : (_CV_TYPE_MAP[fieldPath || ''] || 'string');
            } else if (opEntry) {
                // ── Payload field: resolve from swagger typeMap + tree ──────
                var op     = opEntry.op || opEntry;
                var isResp = backendType === 'response_payload'
                    || backendType === 'RESPONSE_FIELD'
                    || backendType === 'RESPONSE_CONTEXT';  // guard for legacy records
                var rawFields = isResp
                    ? _getResponseFields(op)
                    : ((op.payload && op.payload.request && op.payload.request.fields) || []);
                var typeMap  = _buildFieldTypeMap(rawFields);
                var normPath = (fieldPath || '').replace(/\[\d+\]/g, '');

                valueType = typeMap[normPath] || typeMap[fieldPath]
                    || _typeFromTree(opEntry, opIdx, isResp, normPath)
                    || 'string';
            }

            // ── CONTEXT source: restore bmelPath + bmelSection ────────────────
            // Without these, _buildAllSourcesForBmel uses src.path (the raw
            // backend source_path) to build the bmelRef, which won't match the
            // expression stored at create time. We must replicate the same
            // bmelPath / bmelSection that getCorrelationField() and contextToSource()
            // inject when the user maps manually.
            var ctxBmelPath    = null;
            var ctxBmelSection = null;
            if (frontendType === 'CONTEXT') {
                if (sourceId.indexOf('_CORR_ID') >= 0) {
                    ctxBmelPath    = '$bm.request.correlation_id';
                    ctxBmelSection = 'Request Context';
                } else {
                    // $bm.* platform var — bmelPath = the $bm.* path itself.
                    // Section must mirror _getSourceSection() exactly so the
                    // bmelRef in availableSources matches the stored expression.
                    var rawPath = fieldPath || '';
                    ctxBmelPath = rawPath;
                    if (rawPath.indexOf('$bm.response') === 0) {
                        ctxBmelSection = 'Response Context';
                    } else if (rawPath.indexOf('$bm.token') === 0) {
                        ctxBmelSection = 'Session Token';
                    } else {
                        ctxBmelSection = 'Request Context';
                    }
                }
            }

            return angular.extend({
                id:          sourceId,
                label:       fieldLabel,
                path:        fieldPath || fieldName || '',
                sourceType:  frontendType,
                valueType:   valueType,
                opEntry:     opEntry,
                opIdx:       opIdx,
                // Restore BMEL expression overrides for CONTEXT sources so that
                // _buildAllSourcesForBmel generates the correct bmelRef
                bmelPath:    ctxBmelPath    || undefined,
                bmelSection: ctxBmelSection || undefined,
                fromIntelliSense: true
            }, extra || {});
        }

        function _translateIntelliSenseMappings(apiMappings) {
            var result = [];

            (apiMappings || []).forEach(function (componentMapping) {
                var compId = componentMapping.frame_component_id;
                var compName = componentMapping.frame_field_name;

                (componentMapping.sources || []).forEach(function (src) {
                    // Locate the matching opEntry in selectedOperations
                    var opEntry = null;
                    var opIdx = -1;
                    (ctrl.selectedOperations || []).forEach(function (entry, idx) {
                        var op = entry.op || entry;
                        var opId = op.operationId || op.operation_id || op.id;
                        if (String(opId) === String(src.operation_id)) {
                            opEntry = entry;
                            opIdx = idx;
                        }
                    });

                    // Without a valid opIdx we can't form a DOM-matching ID
                    if (opIdx < 0) { return; }

                    var sourceField = _translateSource(opEntry, opIdx, src.source_type,
                        src.field_path, src.field_name, {
                            // IntelliSense extras — used for badges/tooltips, not for connectors
                            confidence:  src.confidence,
                            explanation: src.explanation
                        });

                    // ── Translate NESTED_TABLE column sub-mappings ─────────────────
                    // The LLM returns column_mappings[] per source; translate to the
                    // columnMappings dict format that recalcConnectors and the
                    // nested-table UI expect: m.columnMappings["Column Name"].source
                    // A column without its own source_type (IntelliSense, legacy
                    // records) lives in the same section as the row array.
                    var columnMappings = null;
                    if (src.column_mappings && src.column_mappings.length > 0) {
                        columnMappings = {};
                        src.column_mappings.forEach(function (cm) {
                            columnMappings[cm.column_name] = {
                                source: _translateSource(opEntry, opIdx,
                                    cm.source_type || src.source_type,
                                    cm.field_path || '', cm.field_name, {
                                        confidence:  cm.confidence,
                                        explanation: cm.explanation
                                    }),
                                // bmel_expression para hijos de 2do nivel (regla e)
                                bmelExpression: cm.bmel_expression || null
                            };
                        });
                    }

                    result.push({
                        targetComponentId:   compId,
                        targetComponentName: compName,
                        source:              sourceField,
                        operation:           opEntry || null,
                        columnMappings:      columnMappings,
                        // Expresión BMEL determinada por IntelliSense (reglas a-d)
                        bmelExpression:      componentMapping.bmel_expression || null
                    });
                });
            });

            return result;
        }


        ctrl.assignToComponent = function (component, operation) {
            if (!ctrl.selectedSourceField) { return; }
            var compId   = component.frame_component_id || component.frame_field_id || component.component_id || component.id;
            var compName = component.frame_field_name   || component.field_name     || component.name          || compId;
            var exists   = ctrl.mappings.some(function (m) {
                return m.targetComponentId === compId && m.source.id === ctrl.selectedSourceField.id;
            });
            if (!exists) {
                ctrl.mappings.push({
                    targetComponentId:   compId,
                    targetComponentName: compName,
                    source:              angular.copy(ctrl.selectedSourceField),
                    operation:           operation || null
                });

                // ── Default BMEL expression (primera conexión) ────────────────
                // Se aplica solo si el target todavía no tiene expresión asignada,
                // de modo que una edición manual previa nunca se sobreescribe.
                //
                // Reglas:
                //   a) source compuesto + target ≠ NESTED_TABLE → bmel:flatten(ref)
                //   b) source compuesto + target = NESTED_TABLE → bmel:self(ref)
                //   c) source escalar              → cualquier target → bmel:self(ref)
                //
                // _buildSourcesForBmel construye el ref canónico
                // ({Op:Section}.$.campo) que buildDefaultExpression necesita.
                if (!ctrl.targetExpressions[compId]) {
                    var bmelSources = _buildSourcesForBmel([angular.copy(ctrl.selectedSourceField)]);
                    if (bmelSources.length) {
                        var _defaultEi = _getExpectedBmelInfo(component);
                        ctrl.targetExpressions[compId] = BmelEditorService.buildDefaultExpression(
                            bmelSources[0],
                            component.frame_component_type || '',
                            _defaultEi ? _defaultEi.type : null
                        );
                    }
                }
                // ─────────────────────────────────────────────────────────────
                // Validate after connection — the default expression may produce a
                // type mismatch (e.g. STRING source → NUMBER target). Without this
                // call bmelErrors stays false and the Create/Apply button stays enabled.
                _validateAllExpressions();
            }
            ctrl.selectedSourceField = null;
        };

        // Returns ALL mappings for a component (multiple sources allowed)
        ctrl.getMappingsForComponent = function (component) {
            var compId = component.frame_component_id || component.frame_field_id || component.component_id || component.id;
            return ctrl.mappings.filter(function (m) { return m.targetComponentId === compId; });
        };

        // Returns first mapping (used for mapped/unmapped state detection)
        ctrl.getMappingForComponent = function (component) {
            return ctrl.getMappingsForComponent(component)[0] || null;
        };

        // Remove a specific source→component mapping
        // _clearExpressionsForComp — removes ALL targetExpressions entries
        // that belong to a given component: the top-level key (compId) and every
        // column-scoped key (compId|col|<colName>).
        function _clearExpressionsForComp(compId) {
            delete ctrl.targetExpressions[compId];
            var prefix = compId + '|col|';
            Object.keys(ctrl.targetExpressions).forEach(function (k) {
                if (k.indexOf(prefix) === 0) { delete ctrl.targetExpressions[k]; }
            });
        }

        // _removeArrayColumns — after removing an array source, also remove all
        // isArrayColumn children mapped to the same targetComponentId whose
        // normalized path starts with the array's normalized path.
        function _removeArrayColumns(compId, arraySourceId) {
            var normArray = _normId(arraySourceId)
                .replace(/^op\d+_(REQUEST_FIELD|RESPONSE_FIELD)_/, '');  // strip prefix → $.documents
            ctrl.mappings = ctrl.mappings.filter(function (m) {
                if (!m.source.isArrayColumn) { return true; }          // keep non-column mappings
                if (m.targetComponentId !== compId) { return true; }   // different component
                var normChild = _normId(m.source.id)
                    .replace(/^op\d+_(REQUEST_FIELD|RESPONSE_FIELD)_/, '');
                return normChild.indexOf(normArray + '.') !== 0;        // keep unrelated children
            });
        }

        ctrl.removeMappingBySource = function (component, sourceId, $event) {
            if ($event) { $event.stopPropagation(); }
            var compId = component.frame_component_id || component.frame_field_id || component.component_id || component.id;
            // Find the mapping being removed to check if it's an array source
            var removing = ctrl.mappings.filter(function (m) {
                return m.targetComponentId === compId && m.source.id === sourceId;
            })[0];
            ctrl.mappings = ctrl.mappings.filter(function (m) {
                return !(m.targetComponentId === compId && m.source.id === sourceId);
            });
            // Cascade: if the removed source was an array, remove its column children
            if (removing && removing.source.valueType === 'array') {
                _removeArrayColumns(compId, sourceId);
            }
            // Limpiar expresiones BMEL (top-level + columnas) si el target queda sin sources
            var stillHasSources = ctrl.mappings.some(function (m) { return m.targetComponentId === compId; });
            if (!stillHasSources) {
                _clearExpressionsForComp(compId);
            }
            _validateAllExpressions();
            $timeout(ctrl.recalcConnectors, 50);
        };

        // Remove ALL mappings for a component (kept for compatibility)
        ctrl.removeMappingForComponent = function (component, $event) {
            if ($event) { $event.stopPropagation(); }
            var compId = component.frame_component_id || component.frame_field_id || component.component_id || component.id;
            ctrl.mappings = ctrl.mappings.filter(function (m) { return m.targetComponentId !== compId; });
            // Limpiar expresiones BMEL (top-level + columnas) — el target queda completamente desvinculado
            _clearExpressionsForComp(compId);
            _validateAllExpressions();
            $timeout(ctrl.recalcConnectors, 50);
        };

        // ── Attribute type lookup for FIELD_REFERENCE components ──────────
        //
        // Frame fields carry attribute_type_id directly; we look it up in
        // templateFull.templateConfig.attribute_types to get scalar_attribute_type.
        // Results are cached by component/column ID to avoid repeated linear scans.

        ctrl._fieldRefTypeCache = {};

        function _getAttrTypeFromTemplate(attrTypeId) {
            if (!attrTypeId || !ctrl.templateFull) { return null; }
            var config = ctrl.templateFull.templateConfig || ctrl.templateFull;
            var attrTypes = config.attribute_types || [];
            for (var i = 0; i < attrTypes.length; i++) {
                if (attrTypes[i].id === attrTypeId) {
                    return attrTypes[i].scalar_attribute_type || null;
                }
            }
            return null;
        }

        // Resource types (with their imported columns) of the target Frame Type's
        // template — what #{…} can name in a BMEL expression.
        function _bmelResourceTypes() {
            return ctrl.templateFull ? BmelEditorService.buildResourceTypes(ctrl.templateFull) : null;
        }

        // Returns allowed enum values for an attribute type, or [] if not ENUM.
        function _getEnumValuesFromTemplate(attrTypeId) {
            if (!attrTypeId || !ctrl.templateFull) { return []; }
            var config    = ctrl.templateFull.templateConfig || ctrl.templateFull;
            var attrTypes = config.attribute_types || [];
            for (var i = 0; i < attrTypes.length; i++) {
                var at = attrTypes[i];
                if (at.id === attrTypeId) {
                    var vals = at.allowed_values || at.enum_values || at.values || [];
                    return Array.isArray(vals) ? vals : [];
                }
            }
            return [];
        }

        // For frame components (FIELD_REFERENCE)
        ctrl.getFieldReferenceType = function (comp) {
            var key = comp.frame_component_id || comp.attribute_type_id;
            if (!key) { return null; }
            if (!ctrl._fieldRefTypeCache.hasOwnProperty(key)) {
                ctrl._fieldRefTypeCache[key] = _getAttrTypeFromTemplate(comp.attribute_type_id);
            }
            return ctrl._fieldRefTypeCache[key];
        };

        // For NESTED_TABLE columns (a SCALAR_INLINE column carries its own type). An OBJECT_FIELD_REF
        // column is mapped from the object's id: its attribute's type isn't the mapping's type.
        ctrl.getColReferenceType = function (col) {
            if (col.column_type === 'SCALAR_INLINE') { return col.scalar_attribute_type || null; }
            if (col.column_type === 'OBJECT_FIELD_REF') { return null; }
            var key = 'col_' + col.attribute_type_id;
            if (!col.attribute_type_id) { return null; }
            if (!ctrl._fieldRefTypeCache.hasOwnProperty(key)) {
                ctrl._fieldRefTypeCache[key] = _getAttrTypeFromTemplate(col.attribute_type_id);
            }
            return ctrl._fieldRefTypeCache[key];
        };

        // ── Frame components (stable reference) ──────────────────────────
        ctrl._emptyFrameComps = [];
        ctrl.getFrameComponents = function () {
            if (!ctrl.selectedFrameType) { return ctrl._emptyFrameComps; }
            var ftId = ctrl.selectedFrameType.frame_type_id;
            if (ctrl._frameCompsId !== ftId) {
                ctrl._frameCompsId = ftId;
                ctrl._frameCompsCache = ctrl.selectedFrameType.frame_fields
                    || ctrl.selectedFrameType.components
                    || ctrl.selectedFrameType.frame_components
                    || ctrl._emptyFrameComps;
                // Invalidate sorted cache when frame type changes
                ctrl._sortedCompsCache = null;
                ctrl._sortedCompsFtId = null;
            }
            return ctrl._frameCompsCache;
        };

        // Returns components sorted by frame_field_seq — stable cached reference
        ctrl._sortedCompsCache = null;
        ctrl._sortedCompsFtId = null;
        ctrl.getSortedComponents = function () {
            if (!ctrl.selectedFrameType) { return ctrl._emptyFrameComps; }
            var ftId = ctrl.selectedFrameType.frame_type_id;
            if (ctrl._sortedCompsFtId !== ftId || !ctrl._sortedCompsCache) {
                ctrl._sortedCompsFtId = ftId;
                ctrl._sortedCompsCache = ctrl.getFrameComponents().slice().sort(function (a, b) {
                    return (a.frame_field_seq || 0) - (b.frame_field_seq || 0);
                });
            }
            return ctrl._sortedCompsCache;
        };

        ctrl.getComponentName = function (c) {
            return c.frame_field_name || c.field_name || c.name || c.component_name
                || c.frame_field_id || c.frame_component_id || c.id || '(unnamed)';
        };

        ctrl.getComponentTooltip = function (comp) {
            var name = ctrl.getComponentName(comp);
            var desc = comp.frame_field_desc || comp.description || comp.desc || '';
            return desc ? (name + '\n' + desc) : name;
        };

        ctrl.getComponentType = function (c) {
            return c.frame_component_type || c.field_type || c.type || c.data_type || 'CUSTOM';
        };

        // ── Nested table expand/collapse ──────────────────────────────────
        ctrl._nestedTableOpen = {};
        ctrl.isNestedTableOpen = function (compId) {
            return ctrl._nestedTableOpen[compId] === true;
        };
        ctrl.toggleNestedTable = function (compId) {
            ctrl._nestedTableOpen[compId] = !ctrl._nestedTableOpen[compId];
            $timeout(ctrl.recalcConnectors, 30);
        };
        ctrl.getNestedColumns = function (comp) {
            return comp.nested_table_columns || [];
        };

        // ── Source field descriptor factory ───────────────────────────────
        function _sourceField(id, label, path, type, valueType, opEntry, opIdx, extra) {
            return Object.assign({
                id: id, label: label, path: path, sourceType: type,
                valueType: valueType || 'string', opEntry: opEntry, opIdx: opIdx
            }, extra || {});
        }

        // ── Simple sources (headers, params) ─────────────────────────────
        function _computeSimpleSources(entry, sourceType, opIdx) {
            var prefix = 'op' + opIdx + '_' + sourceType + '_';
            switch (sourceType) {
                case 'HEADER':
                    return (entry.op.httpHeaders || []).map(function (h) {
                        return _sourceField(prefix + h.name, h.name, h.name, 'HEADER', 'string', entry, opIdx);
                    });
                case 'URI_PARAM':
                    return (entry.op.uriParams || []).map(function (p) {
                        return _sourceField(prefix + p.name, p.name, p.name, 'URI_PARAM', p.type || 'string', entry, opIdx);
                    });
                case 'QUERY_PARAM':
                    return (entry.op.queryParams || []).map(function (p) {
                        return _sourceField(prefix + p.name, p.name, p.name, 'QUERY_PARAM', p.type || 'string', entry, opIdx);
                    });
                case 'CUSTOM_HEADER':
                    var customs = [];
                    if (entry.correlationKey.sourceType === 'CUSTOM_HEADER' && entry.correlationKey.sourcePath) {
                        customs.push(_sourceField(prefix + entry.correlationKey.sourcePath,
                            entry.correlationKey.sourcePath, entry.correlationKey.sourcePath,
                            'CUSTOM_HEADER', 'string', entry, opIdx));
                    }
                    (entry.filters || []).forEach(function (f) {
                        if (f.filterType === 'CUSTOM_HEADER' && f.sourcePath &&
                            !customs.some(function (c) { return c.path === f.sourcePath; })) {
                            customs.push(_sourceField(prefix + f.sourcePath, f.sourcePath, f.sourcePath,
                                'CUSTOM_HEADER', 'string', entry, opIdx));
                        }
                    });
                    return customs;
            }
            return [];
        }

        ctrl.getSimpleSources = function (entry, sourceType) {
            var opIdx = ctrl.selectedOperations.indexOf(entry);
            var key = opIdx + '_' + sourceType;
            if (!ctrl._simpleSourcesCache[key]) {
                ctrl._simpleSourcesCache[key] = _computeSimpleSources(entry, sourceType, opIdx);
            }
            return ctrl._simpleSourcesCache[key];
        };

        ctrl.treeNodeToSource = function (node, entry, side) {
            var opIdx = ctrl.selectedOperations.indexOf(entry);
            var sType = side === 'request' ? 'REQUEST_FIELD' : 'RESPONSE_FIELD';
            var prefix = 'op' + opIdx + '_' + sType + '_';
            return _sourceField(prefix + node.path, node.label + ' (' + node.path + ')',
                node.path, sType, node.valueType, entry, opIdx,
                { depth: node.depth });   // needed for first-level array validation
        };

        // ── Array column auto-mapper ──────────────────────────────────────────
        // When an array source is connected to a frame component, each immediate
        // leaf child is automatically mapped to the same target with
        // valueType "column:<baseType>" so BMEL aggregation functions can reference
        // typed column sources (AVG, MAX, MIN, SUM, COUNT, etc.).
        function _autoMapArrayColumns(arrayField, targetComp) {
            var side = arrayField.sourceType === 'REQUEST_FIELD' ? 'request' : 'response';
            var key  = arrayField.opIdx + '_' + side;
            var tree = ctrl._treeCache[key];
            if (!tree) { return; }

            // Normalize both sides so that IntelliSense paths ($.a.b, no indices)
            // match tree-cache paths built from JSON sample ($.a[0].b).
            function _findNode(nodes, path) {
                var normPath = path.replace(/\[\d+\]/g, '');
                for (var i = 0; i < nodes.length; i++) {
                    var n = nodes[i];
                    var normNodePath = (n.path || '').replace(/\[\d+\]/g, '');
                    if (n.path === path || normNodePath === normPath) { return n; }
                    if (n._children && n._children.length) {
                        var found = _findNode(n._children, path);
                        if (found) { return found; }
                    }
                }
                return null;
            }

            var arrayNode = _findNode(tree, arrayField.path);
            if (!arrayNode || !arrayNode._children || !arrayNode._children.length) { return; }

            // Skip the [0] index-wrapper object produced by JSON sample parsing
            var leafCandidates = arrayNode._children;
            if (leafCandidates.length === 1 && leafCandidates[0].valueType === 'object') {
                leafCandidates = leafCandidates[0]._children || [];
            }

            var opIdx    = arrayField.opIdx;
            var opEntry  = arrayField.opEntry;
            var sType    = arrayField.sourceType;
            var prefix   = 'op' + opIdx + '_' + sType + '_';
            var targetId = String(targetComp.frame_component_id || '');
            var targetName = targetComp.frame_field_name || targetComp.name || targetId;

            leafCandidates.forEach(function (child) {
                if (!child.isLeaf) { return; }
                var colSource = _sourceField(
                    prefix + child.path,
                    child.label + ' (' + child.path + ')',
                    child.path, sType,
                    'column:' + child.valueType,
                    opEntry, opIdx,
                    { depth: child.depth, isArrayColumn: true }
                );
                var alreadyMapped = ctrl.mappings.some(function (m) {
                    return m.targetComponentId === targetId && m.source.id === colSource.id;
                });
                if (!alreadyMapped) {
                    ctrl.mappings.push({
                        targetComponentId:   targetId,
                        targetComponentName: targetName,
                        source:              colSource,
                        operation:           opEntry || null
                    });
                }
            });
        }

        // ── _getArrayColumnSources ────────────────────────────────────────────────
        // Builds virtual column source objects for an array field's leaf children
        // WITHOUT touching ctrl.mappings. Used exclusively by _buildAllSourcesForBmel
        // so that BMEL aggregation functions can reference typed column sources
        // (AVG, MAX, MIN, SUM, COUNT, …) even though the children are not formally
        // linked in the mapping step.
        //
        // This replaces the old _autoMapArrayColumns → ctrl.mappings.push() pattern:
        // the children are now ephemeral — generated on demand at editor-open time.
        function _getArrayColumnSources(arrayField) {
            // The whole MANY operation has no payload-tree children of its own —
            // its per-invocation fields are mapped explicitly as table columns.
            if (arrayField.sourceType === 'OPERATION') { return []; }
            var side = arrayField.sourceType === 'REQUEST_FIELD' ? 'request' : 'response';
            var key  = arrayField.opIdx + '_' + side;
            if (!ctrl._treeCache[key]) {
                ctrl.getPayloadTree(arrayField.opEntry, side);
            }
            var tree = ctrl._treeCache[key];
            if (!tree) { return []; }

            // Normalize both sides so that IntelliSense paths ($.a.b, no indices)
            // match tree-cache paths built from JSON sample ($.a[0].b).
            function _findNode(nodes, path) {
                var normPath = path.replace(/\[\d+\]/g, '');
                for (var i = 0; i < nodes.length; i++) {
                    var n = nodes[i];
                    var normNodePath = (n.path || '').replace(/\[\d+\]/g, '');
                    if (n.path === path || normNodePath === normPath) { return n; }
                    if (n._children && n._children.length) {
                        var f = _findNode(n._children, path);
                        if (f) { return f; }
                    }
                }
                return null;
            }

            var arrayNode = _findNode(tree, arrayField.path);
            if (!arrayNode || !arrayNode._children || !arrayNode._children.length) { return []; }

            var leafCandidates = arrayNode._children;
            if (leafCandidates.length === 1 && leafCandidates[0].valueType === 'object') {
                leafCandidates = leafCandidates[0]._children || [];
            }

            var opIdx   = arrayField.opIdx;
            var opEntry = arrayField.opEntry;
            var sType   = arrayField.sourceType;
            var prefix  = 'op' + opIdx + '_' + sType + '_';
            var result  = [];

            leafCandidates.forEach(function (child) {
                if (!child.isLeaf) { return; }
                result.push(_sourceField(
                    prefix + child.path,
                    child.label + ' (' + child.path + ')',
                    child.path, sType,
                    'column:' + child.valueType,
                    opEntry, opIdx,
                    { depth: child.depth, isArrayColumn: true }
                ));
            });
            return result;
        }

        // Correlation ID field — represents the operation's correlationKey as a source
        ctrl.getCorrelationField = function (opIdx, entry) {
            var cacheKey = opIdx + '_CORR';
            if (ctrl._simpleSourcesCache.hasOwnProperty(cacheKey)) {
                return ctrl._simpleSourcesCache[cacheKey];
            }
            var ck = entry && entry.correlationKey;
            if (!ck || !ck.sourcePath) {
                ctrl._simpleSourcesCache[cacheKey] = null;
                return null;
            }
            var field = _sourceField(
                'op' + opIdx + '_CORR_ID',
                'Correlation ID',
                ck.sourcePath,
                'CONTEXT', 'string', entry, opIdx,
                {
                    icon:        'tag',
                    desc:        ck.sourcePath + ' (' + (ck.sourceType || 'custom') + ')',
                    // BMEL ref overrides: the canonical context path and its section.
                    // ck.sourcePath (e.g. 'X-Transaction-Id') is the raw HTTP header
                    // name and must NOT appear in the BMEL expression — the runtime
                    // resolves correlation_id via $bm.request.correlation_id regardless
                    // of which header carries it.
                    bmelPath:    '$bm.request.correlation_id',
                    bmelSection: 'Request Context'
                }
            );
            ctrl._simpleSourcesCache[cacheKey] = field;
            return field;
        };

        // ── Mapping validation rules ──────────────────────────────────────
        function _validateConnection(field, comp) {
            if (!field || !comp) return { valid: true };
            var compType = comp.frame_component_type || 'CUSTOM';
            var srcType = field.sourceType;
            var valType = field.valueType;
            var isPayload = srcType === 'REQUEST_FIELD' || srcType === 'RESPONSE_FIELD';
            var isArray = valType === 'array';
            var isObject = valType === 'object';
            var isFirst = field.depth === 0;
            var isOpArray = srcType === 'OPERATION';
            var isMany = _isManySource(field);

            // Array child nodes (path contains [N]) can only map to NESTED_TABLE
            // column slots — validated separately by _validateColumnConnection.
            // Connecting them to any main frame component is not allowed.
            if (/\[\d+\]/.test(field.path || '')) {
                return { valid: false, reason: 'Los hijos de un array solo pueden conectarse a columnas de un NESTED_TABLE' };
            }

            // NESTED_TABLE: solo acepta array de primer nivel de payload
            if (compType === 'NESTED_TABLE') {
                // Ya tiene un source — no se puede agregar otro
                var compId = comp.frame_component_id || comp.id;
                var alreadyMapped = ctrl.mappings.some(function (m) { return m.targetComponentId === compId; });
                if (alreadyMapped) return { valid: false, reason: 'NESTED_TABLE ya tiene un array source — desvincula el actual primero' };
                // MANY operation: the operation itself is the first-level array
                // (one row per invocation); any array inside it is already nested.
                if (isOpArray) return { valid: true };
                if (isMany) return { valid: false, reason: 'Operación con cardinalidad MANY — conecta la operación completa ([]) como fuente de filas del NESTED_TABLE' };
                if (!isPayload) return { valid: false, reason: 'NESTED_TABLE requiere un source del payload (request o response)' };
                if (!isArray) return { valid: false, reason: 'NESTED_TABLE requiere un campo array — leaf fields y objects no son válidos aquí' };
                if (!isFirst) return { valid: false, reason: 'NESTED_TABLE requiere un array de primer nivel — arrays anidados no son soportados' };
                return { valid: true };
            }

            // MANY operation → every source is an array of N occurrences, so the
            // same rules as payload arrays apply (FLATTEN / aggregation functions)
            if (isMany) return { valid: true, operation: 'FLATTEN' };

            // Arrays payload → FLATTEN (cualquier profundidad contra CUSTOM/FIELD_REF/OBJECT_REF)
            if (isArray && isPayload) return { valid: true, operation: 'FLATTEN' };

            // Objects payload → FLATTEN
            if (isObject && isPayload) return { valid: true, operation: 'FLATTEN' };

            return { valid: true };
        }

        // Verifica si fieldPath es DESCENDIENTE del arrayPath sin cruzar otro array anidado.
        // Correcto:   $.ORDERS[0].customer.id          → resto '.customer.id'     — sin [N] adicional ✓
        // Correcto:   $.ORDERS[0].params.param_name    → resto '.params.param_name' — sin [N] ✓
        // Bloqueado:  $.ORDERS[0].DETAILS[0].item_id   → resto '.DETAILS[0].item_id' — tiene [0] ✗
        function _isDescendantWithoutNestedArray(fieldPath, arrayPath) {
            // Normalizar arrayPath (quitar índices existentes, ej. si se guardó con [N])
            var normBase = arrayPath.replace(/\[\d+\]/g, '');
            // Escapar caracteres especiales de regex ($, ., etc.)
            var escaped = normBase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            // El fieldPath debe empezar con normBase[N]
            var m = fieldPath.match(new RegExp('^' + escaped + '\\[\\d+\\](.*)$'));
            if (!m) return false;
            // El resto NO debe contener otro [N] (array anidado)
            return !/\[\d+\]/.test(m[1]);
        }

        // Valida conectar un field a una columna de NESTED_TABLE.
        // Arrays y objects están permitidos: al conectar un array, sus hijos escalares
        // se auto-mapean a las columnas restantes del mismo NESTED_TABLE
        // (ver _autoMapChildrenToColumns). Objects se permiten para casos donde el
        // usuario quiere referenciar la estructura completa.
        function _validateColumnConnection(field, colTarget) {
            if (!field || !colTarget) return { valid: false, reason: 'Target de columna inválido' };
            var mainMapping = null;
            for (var i = 0; i < ctrl.mappings.length; i++) {
                if (String(ctrl.mappings[i].targetComponentId) === String(colTarget.compId)) {
                    mainMapping = ctrl.mappings[i]; break;
                }
            }

            // Rows = invocations of a MANY operation: a column can take any
            // per-invocation value of that same operation — payload fields,
            // request/response context, headers, params — as long as it isn't
            // inside a payload array (that would be a nested array).
            if (mainMapping && mainMapping.source.sourceType === 'OPERATION') {
                if (field.sourceType === 'OPERATION') return { valid: false, reason: 'La operación ya es la fuente de filas de este NESTED_TABLE' };
                if (field.opIdx !== mainMapping.source.opIdx) {
                    return { valid: false, reason: 'La columna debe provenir de la operación ' + _opDisplayName(mainMapping.source.opEntry, mainMapping.source.opIdx) };
                }
                // A value inside one of the operation's own arrays is a list per
                // invocation: the column takes its first element (bmel:first). An
                // array itself would be a second row level — not supported.
                if (/\[\d+\]/.test(field.path || '') && field.valueType === 'array') {
                    return { valid: false, reason: 'Arrays anidados no son soportados en un NESTED_TABLE por invocación' };
                }
                return { valid: true };
            }

            var isPayload = field.sourceType === 'REQUEST_FIELD' || field.sourceType === 'RESPONSE_FIELD';
            if (!isPayload) return { valid: false, reason: 'Los column mappings requieren campos del payload' };
            if (!mainMapping) return { valid: false, reason: 'El NESTED_TABLE debe estar conectado a un array source primero' };
            if (!_isDescendantWithoutNestedArray(field.path, mainMapping.source.path)) {
                return { valid: false, reason: 'El campo debe ser hijo directo de: ' + mainMapping.source.path };
            }
            return { valid: true };
        }

        // Pre-compute invalid components when drawing starts
        ctrl._connectingInvalidMap = {};
        function _computeInvalidMap(field) {
            ctrl._connectingInvalidMap = {};
            ctrl.getSortedComponents().forEach(function (comp) {
                if (_validateConnection(field, comp).valid) return; // valid — skip
                var compId = comp.frame_component_id || comp.id;
                // Don't gray-out a NESTED_TABLE that already has an array source
                // when the field is a valid child of that array — columns are still valid targets
                if (comp.frame_component_type === 'NESTED_TABLE') {
                    var mainM = ctrl.mappings.filter(function (m) { return m.targetComponentId === compId; })[0];
                    if (mainM && mainM.source.sourceType === 'OPERATION'
                            && _validateColumnConnection(field, { compId: compId }).valid) {
                        return; // per-invocation columns are accessible
                    }
                    var isPayload = field.sourceType === 'REQUEST_FIELD' || field.sourceType === 'RESPONSE_FIELD';
                    var isLeaf = field.valueType !== 'array' && field.valueType !== 'object';
                    if (mainM && isPayload && isLeaf && _isDescendantWithoutNestedArray(field.path, mainM.source.path)) {
                        return; // columns are accessible — don't block this component
                    }
                }
                ctrl._connectingInvalidMap[compId] = true;
            });
        }

        ctrl.isCompInvalid = function (comp) {
            return !!(ctrl.connecting && ctrl._connectingInvalidMap[comp.frame_component_id || comp.id]);
        };

        // ── Mapping label ─────────────────────────────────────────────────
        function _getSourceSection(source) {
            switch (source.sourceType) {
                case 'HEADER': return 'HTTP Headers';
                case 'CUSTOM_HEADER': return 'Custom Headers';
                case 'URI_PARAM': return 'URI Parameters';
                case 'QUERY_PARAM': return 'Query Parameters';
                case 'REQUEST_FIELD': return 'Request Payload';
                case 'RESPONSE_FIELD': return 'Response Payload';
                case 'OPERATION': return _OPERATION_SECTION;
                case 'CONTEXT':
                    if (source.path && source.path.indexOf('$bm.response') === 0) return 'Response Context';
                    if (source.path && source.path.indexOf('$bm.token') === 0) return 'Session Token';
                    if (source.id && source.id.indexOf('_CORR_ID') >= 0) return 'Correlation ID';
                    return 'Request Context';
                default: return source.sourceType || 'Source';
            }
        }

        // Section as written in labels and BMEL refs — a MANY operation's
        // sections carry "[]" (array of N invocations): "Response Payload[]".
        // bmelSection overrides (context vars / correlation id) win, as before.
        function _sectionLabel(source, forBmel) {
            var section = (forBmel && source.bmelSection) || _getSourceSection(source);
            return _isManySource(source) ? section + '[]' : section;
        }

        // Format: #{Source Section}.path  (array indices normalized out)
        ctrl.getMappingLabel = function (source) {
            var section = _sectionLabel(source);
            var path = (source.path || '').replace(/\[\d+\]/g, '');
            return '#{' + section + '}.' + path;
        };

        // Expose section and path separately for colored rendering in template
        ctrl.getMappingSection = function (source) { return _sectionLabel(source); };
        ctrl.getMappingPath = function (source) {
            return (source.path || '').replace(/\[\d+\]/g, '');
        };

        // Devuelve el contenido entre llaves: "OpName:SectionName"
        ctrl.getMappingOpSectionLabel = function (source) {
            var opName = '';
            if (source.opIdx !== undefined && ctrl.selectedOperations && ctrl.selectedOperations[source.opIdx]) {
                var entry = ctrl.selectedOperations[source.opIdx];
                opName = (entry.op && (entry.op.operationName || entry.op.summary || entry.op.path)) || '';
            }
            var section = _sectionLabel(source);
            return opName ? opName + ':' + section : section;
        };

        // ── Column sub-mapping helpers ────────────────────────────────────
        ctrl.connectingColTarget = null;
        ctrl.connectingColValid = null;

        ctrl.getNestedTableArraySource = function (comp) {
            var compId = comp.frame_component_id || comp.id;
            var m = ctrl.mappings.filter(function (x) { return x.targetComponentId === compId; })[0];
            return m ? m.source : null;
        };

        // Badge text for a NESTED_TABLE's row source: the array path, or
        // "Op []" when the rows are the N invocations of a MANY operation.
        ctrl.getNestedTableArrayLabel = function (comp) {
            var src = ctrl.getNestedTableArraySource(comp);
            if (!src) { return ''; }
            return src.sourceType === 'OPERATION'
                ? _opDisplayName(_opEntryOf(src), src.opIdx) + ' [] — one row per invocation'
                : src.path;
        };

        ctrl.getColMapping = function (comp, colName) {
            var compId = comp.frame_component_id || comp.id;
            var m = ctrl.mappings.filter(function (x) { return x.targetComponentId === compId; })[0];
            return (m && m.columnMappings && m.columnMappings[colName]) || null;
        };

        ctrl.removeColMapping = function (comp, colName, $event) {
            if ($event) { $event.stopPropagation(); }
            var compId = comp.frame_component_id || comp.id;
            var m = ctrl.mappings.filter(function (x) { return x.targetComponentId === compId; })[0];
            if (m && m.columnMappings) { delete m.columnMappings[colName]; }
            // The column's own BMEL expression (if any) is now orphaned — its source
            // is gone, so leaving it in targetExpressions makes _validateAllExpressions
            // flag it as a dangling reference forever. Clear it, same as every other
            // unlink path (removeMappingByField/BySource/ForComponent) already does
            // for their own targets — this one was missing both the cleanup and the
            // re-validation call, so the error triangle used to survive the unlink.
            if (ctrl.targetExpressions) { delete ctrl.targetExpressions[compId + '|col|' + colName]; }
            _validateAllExpressions();
            $timeout(ctrl.recalcConnectors, 50);
        };

        ctrl.isColWireTarget = function (comp, colName) {
            if (!ctrl.connectingColTarget) return false;
            return String(ctrl.connectingColTarget.compId) === String(comp.frame_component_id || comp.id)
                && ctrl.connectingColTarget.colName === colName;
        };

        // ── Section collapse state ────────────────────────────────────────
        ctrl._sectionState = {};
        ctrl.isSectionOpen = function (key) { return ctrl._sectionState[key] !== false; };
        ctrl.toggleSection = function (key) {
            ctrl._sectionState[key] = !ctrl.isSectionOpen(key);
            $timeout(ctrl.recalcConnectors, 30);
        };

        var _OP_GROUP_SUFFIXES = [
            '_req_ctx', '_res_ctx',
            '_headers', '_custom_hdrs', '_uri_params', '_query_params',
            '_req_payload', '_res_payload'
        ];

        // Opens / closes an operation together with every group inside it.
        function _setOpOpen(opIdx, open) {
            ctrl._sectionState['op_' + opIdx] = open;
            _OP_GROUP_SUFFIXES.forEach(function (s) { ctrl._sectionState['op_' + opIdx + s] = open; });
        }

        // Every node of the operation's request / response payload trees, at any depth —
        // the trees start collapsed below the second level (_buildTree).
        function _expandPayloadTrees(opIdx) {
            var entry = ctrl.selectedOperations[opIdx];
            if (!entry) { return; }
            ['request', 'response'].forEach(function (side) {
                ctrl.getPayloadTree(entry, side);          // builds the tree if not cached yet
                (function open(nodes) {
                    (nodes || []).forEach(function (node) {
                        if (node.hasChildren) { node.collapsed = false; }
                        open(node._children);
                    });
                })(ctrl._treeCache[opIdx + '_' + side]);
                delete ctrl._flattenCache[opIdx + '_' + side];
            });
        }

        ctrl.expandAll = function () {
            ctrl.selectedOperations.forEach(function (entry, opIdx) {
                _setOpOpen(opIdx, true);
                _expandPayloadTrees(opIdx);
            });
            $timeout(ctrl.recalcConnectors, 30);
        };

        ctrl.collapseAll = function () {
            ctrl.selectedOperations.forEach(function (entry, opIdx) { _setOpOpen(opIdx, false); });
            $timeout(ctrl.recalcConnectors, 30);
        };

        // A single operation's header buttons: expand opens the operation and every group
        // inside it; collapse folds only the groups inside — the operation stays open.
        ctrl.expandOp = function (opIdx, $event) {
            if ($event) { $event.stopPropagation(); }
            _setOpOpen(opIdx, true);
            _expandPayloadTrees(opIdx);
            $timeout(ctrl.recalcConnectors, 30);
        };

        ctrl.collapseOp = function (opIdx, $event) {
            if ($event) { $event.stopPropagation(); }
            _OP_GROUP_SUFFIXES.forEach(function (s) { ctrl._sectionState['op_' + opIdx + s] = false; });
            $timeout(ctrl.recalcConnectors, 30);
        };

        // ── Wire-connection system ────────────────────────────────────────
        // State
        ctrl.connecting = null;   // { field, startX, startY } while gesture is active
        ctrl.connectingTarget = null;   // component currently under cursor
        // SVG path for the live arrow
        ctrl._connectors = [];     // permanent connectors [{id,d,x1,y1,x2,y2}]

        function _arrowPath(x1, y1, x2, y2) {
            var cp = Math.min(Math.abs(x2 - x1) * 0.55, 150);
            return 'M' + x1.toFixed(1) + ',' + y1.toFixed(1)
                + ' C' + (x1 + cp).toFixed(1) + ',' + y1.toFixed(1)
                + '  ' + (x2 - cp).toFixed(1) + ',' + y2.toFixed(1)
                + '  ' + x2.toFixed(1) + ',' + y2.toFixed(1);
        }

        // Called on mousedown on a source field
        ctrl.startConnect = function (field, $event) {
            $event.preventDefault();

            var bodyEl = document.querySelector('.cbew-mapping-body');
            if (!bodyEl) return;
            var bodyRect = bodyEl.getBoundingClientRect();

            var srcEl = $event.target;
            while (srcEl && !srcEl.classList.contains('cbew-map-field') && !srcEl.classList.contains('cbew-map-tree-parent')
                    && !srcEl.classList.contains('cbew-map-op-array')) {
                if (!srcEl.parentElement) break;
                srcEl = srcEl.parentElement;
            }

            var startX, startY;
            if (srcEl) {
                var r = srcEl.getBoundingClientRect();
                startX = r.right - bodyRect.left;
                startY = r.top + r.height / 2 - bodyRect.top;
            } else {
                startX = $event.clientX - bodyRect.left;
                startY = $event.clientY - bodyRect.top;
            }

            ctrl.connecting = { field: field, startX: startX, startY: startY };
            ctrl.connectingTarget = null;
            ctrl.connectingValid = null;
            ctrl._liveWire = { d: _buildPath(startX, startY, startX + 30, startY), x1: startX, y1: startY, x2: startX + 30, y2: startY, snapped: false };

            // Pre-compute which components are invalid for this source
            _computeInvalidMap(field);

            document.addEventListener('mousemove', _onConnectMove);
            document.addEventListener('mouseup', _onConnectUp);
        };

        function _onConnectMove(e) {
            if (!ctrl.connecting) return;
            var bodyEl = document.querySelector('.cbew-mapping-body');
            if (!bodyEl) return;
            var bodyRect = bodyEl.getBoundingClientRect();
            var mx = e.clientX - bodyRect.left;
            var my = e.clientY - bodyRect.top;

            var el = document.elementFromPoint(e.clientX, e.clientY);
            var newTarget = null;
            var newColTarget = null;
            var walk = el;
            while (walk) {
                if (walk.getAttribute) {
                    // Column slot takes priority
                    if (walk.getAttribute('data-col-comp') && walk.getAttribute('data-col-name')) {
                        newColTarget = { compId: walk.getAttribute('data-col-comp'), colName: walk.getAttribute('data-col-name') };
                        break;
                    }
                    var compId = walk.getAttribute('data-comp-id');
                    if (compId) {
                        var comps = ctrl.getSortedComponents();
                        for (var i = 0; i < comps.length; i++) {
                            if (String(comps[i].frame_component_id || i) === compId) { newTarget = comps[i]; break; }
                        }
                        break;
                    }
                }
                if (walk.classList && walk.classList.contains('cbew-mapping-body')) break;
                walk = walk.parentElement;
            }

            ctrl.connectingTarget = newColTarget ? null : newTarget;
            ctrl.connectingColTarget = newColTarget;
            ctrl.connectingValid = newColTarget
                ? _validateColumnConnection(ctrl.connecting.field, newColTarget).valid
                : (newTarget ? _validateConnection(ctrl.connecting.field, newTarget).valid : null);

            // Snap endpoint to target element
            var snapEl = newColTarget
                ? bodyEl.querySelector('[data-col-comp="' + newColTarget.compId + '"][data-col-name="' + newColTarget.colName + '"]')
                : (newTarget ? bodyEl.querySelector('[data-comp-id="' + (newTarget.frame_component_id || '') + '"]') : null);
            if (snapEl) {
                var sr = snapEl.getBoundingClientRect();
                mx = sr.left - bodyRect.left + 6;
                my = sr.top + sr.height / 2 - bodyRect.top;
            }

            ctrl._liveWire = {
                d: _buildPath(ctrl.connecting.startX, ctrl.connecting.startY, mx, my),
                x1: ctrl.connecting.startX, y1: ctrl.connecting.startY, x2: mx, y2: my,
                snapped: !!(newTarget || newColTarget),
                invalid: ctrl.connectingValid === false
            };
            $scope.$apply();
        }

        function _onConnectUp() {
            document.removeEventListener('mousemove', _onConnectMove);
            document.removeEventListener('mouseup', _onConnectUp);

            var field = ctrl.connecting && ctrl.connecting.field;
            var target = ctrl.connectingTarget;
            var colTarget = ctrl.connectingColTarget;

            ctrl.connecting = null;
            ctrl.connectingTarget = null;
            ctrl.connectingColTarget = null;
            ctrl.connectingValid = null;
            ctrl._liveWire = null;
            ctrl._connectingInvalidMap = {};

            if (field && colTarget) {
                // ── Column sub-mapping ──
                var cv = _validateColumnConnection(field, colTarget);
                if (!cv.valid) {
                    $mdToast.show($mdToast.simple().textContent('⛔ ' + cv.reason).position('top right').hideDelay(3500));
                } else {
                    var mainM = ctrl.mappings.filter(function (m) { return String(m.targetComponentId) === String(colTarget.compId); })[0];
                    if (mainM) {
                        if (!mainM.columnMappings) mainM.columnMappings = {};
                        mainM.columnMappings[colTarget.colName] = { source: angular.copy(field) };
                        // Per-invocation table + a value inside an array of the operation:
                        // one element per row — the first one (see _validateColumnConnection).
                        $timeout(ctrl.recalcConnectors, 40);
                        if (mainM.source.sourceType === 'OPERATION' && _isInsideArray(field)) {
                            ctrl.targetExpressions[colTarget.compId + '|col|' + colTarget.colName] =
                                _firstElementExpression(field, true);
                            _validateAllExpressions();
                        }
                    }
                }
            } else if (field && target) {
                // ── Component mapping ──
                var v = _validateConnection(field, target);
                if (!v.valid) {
                    $mdToast.show($mdToast.simple().textContent('⛔ ' + v.reason).position('top right').hideDelay(3500));
                } else {
                    ctrl.selectedSourceField = field;
                    ctrl._justConnected = true;
                    $timeout(function () { ctrl._justConnected = false; }, 300);
                    ctrl.assignToComponent(target, v.operation);
                    // Auto-expand NESTED_TABLE after connecting its array source
                    if (target.frame_component_type === 'NESTED_TABLE') {
                        ctrl._nestedTableOpen[target.frame_component_id || target.id] = true;
                    }
                    // NOTE: array children are NOT auto-mapped into ctrl.mappings.
                    // They are generated on demand as virtual column sources when
                    // the BMEL editor opens — see _getArrayColumnSources / _buildAllSourcesForBmel.
                    $timeout(ctrl.recalcConnectors, 40);
                }
            }

            $scope.$apply();
        }

        ctrl.isDrawingNow = function () { return !!ctrl.connecting; };
        ctrl.isWireTarget = function (comp) {
            if (!ctrl.connectingTarget) return false;
            return String(ctrl.connectingTarget.frame_component_id || '') === String(comp.frame_component_id || '');
        };

        // A component's head is stuck when it sits at the top of the panel while
        // the rest of the component (its sources) scrolls under it.
        function _updateStuckHeads() {
            var tgtPanel = document.querySelector('.cbew-map-components');
            if (!tgtPanel) { return; }
            var top = tgtPanel.getBoundingClientRect().top;
            tgtPanel.querySelectorAll('.cbew-map-comp-head').forEach(function (h) {
                var row = h.closest('.cbew-map-component');
                var stuck = !!row && !row.classList.contains('cbew-map-component--sticky') &&
                    row.getBoundingClientRect().top < top - 1 && h.getBoundingClientRect().top <= top + 1;
                h.classList.toggle('cbew-map-comp-head--stuck', stuck);
            });
        }

        // Recalculate permanent connector positions from DOM
        ctrl.recalcConnectors = function () {
            var bodyEl = document.querySelector('.cbew-mapping-body');
            if (!bodyEl) return;
            _updateStuckHeads();
            var bodyRect = bodyEl.getBoundingClientRect();
            var out = [];

            ctrl.mappings.forEach(function (m) {
                // Skip if the source operation section is collapsed — no connectors from a
                // collapsed operation, not even from the whole-operation array handle
                // (MANY cardinality), although that one lives in the always-visible header.
                var opCollapsed = m.source.opIdx !== undefined && !ctrl.isSectionOpen('op_' + m.source.opIdx);
                if (opCollapsed) return;

                // ── Main component connector ──────────────────────────────
                var srcEl = bodyEl.querySelector('[data-field-id="' + m.source.id + '"]');
                var tgtEl = bodyEl.querySelector('[data-comp-id="' + m.targetComponentId + '"]');
                if (srcEl && tgtEl) {
                    var sr = srcEl.getBoundingClientRect();
                    var tr = tgtEl.getBoundingClientRect();
                    var x1 = sr.right - bodyRect.left;
                    // The whole-operation [] handle sits inside the header's second line:
                    // leave from the header's right edge (like every field row), at its height.
                    var opHeader = m.source.sourceType === 'OPERATION' && srcEl.closest('.cbew-map-section__header');
                    if (opHeader) { x1 = opHeader.getBoundingClientRect().right - bodyRect.left; }
                    var y1 = sr.top + sr.height / 2 - bodyRect.top;
                    var x2 = tr.left - bodyRect.left;
                    var y2 = tr.top + tr.height / 2 - bodyRect.top;
                    // A component scrolled past its middle: arrive at its stuck head
                    var stuckHead = tgtEl.querySelector('.cbew-map-comp-head--stuck');
                    if (stuckHead) {
                        var hr = stuckHead.getBoundingClientRect();
                        y2 = hr.top + hr.height / 2 - bodyRect.top;
                    }
                    out.push({
                        id: m.source.id + '|' + m.targetComponentId,
                        d: _arrowPath(x1, y1, x2, y2), x1: x1, y1: y1, x2: x2, y2: y2,
                        cx: (x1 + x2) / 2, cy: (y1 + y2) / 2
                    });
                }

                // ── Column sub-mapping connectors ─────────────────────────
                if (m.columnMappings) {
                    Object.keys(m.columnMappings).forEach(function (colName) {
                        var cm = m.columnMappings[colName];
                        if (!cm || !cm.source) return;
                        // Also skip if source op is collapsed
                        if (cm.source.opIdx !== undefined && !ctrl.isSectionOpen('op_' + cm.source.opIdx)) return;
                        var colSrcEl = bodyEl.querySelector('[data-field-id="' + cm.source.id + '"]');
                        // Fallback: IntelliSense paths use the flat API path ($.documents.documentType)
                        // but the tree renders with array-indexed paths ($.documents[0].documentType).
                        // Normalize both sides by stripping array indices before comparing.
                        if (!colSrcEl) {
                            var normalizedCmId = cm.source.id.replace(/\[\d+\]/g, '');
                            var allFieldEls = bodyEl.querySelectorAll('[data-field-id]');
                            for (var _fi = 0; _fi < allFieldEls.length; _fi++) {
                                if (allFieldEls[_fi].getAttribute('data-field-id').replace(/\[\d+\]/g, '') === normalizedCmId) {
                                    colSrcEl = allFieldEls[_fi];
                                    break;
                                }
                            }
                        }
                        var colTgtEl = bodyEl.querySelector('[data-col-comp="' + m.targetComponentId + '"][data-col-name="' + colName + '"]');
                        if (!colSrcEl || !colTgtEl) return;
                        var csr = colSrcEl.getBoundingClientRect();
                        var ctr = colTgtEl.getBoundingClientRect();
                        // A column scrolled under its table's sticky component row isn't visible
                        var stickyRow = colTgtEl.closest('.cbew-map-comp-group');
                        stickyRow = stickyRow && stickyRow.querySelector('.cbew-map-component--sticky');
                        if (stickyRow && ctr.top + ctr.height / 2 < stickyRow.getBoundingClientRect().bottom) return;
                        var cx1 = csr.right - bodyRect.left;
                        var cy1 = csr.top + csr.height / 2 - bodyRect.top;
                        var cx2 = ctr.left - bodyRect.left;
                        var cy2 = ctr.top + ctr.height / 2 - bodyRect.top;
                        out.push({
                            id: cm.source.id + '|col|' + m.targetComponentId + '|' + colName,
                            d: _arrowPath(cx1, cy1, cx2, cy2), x1: cx1, y1: cy1, x2: cx2, y2: cy2,
                            cx: (cx1 + cx2) / 2, cy: (cy1 + cy2) / 2
                        });
                    });
                }
            });
            ctrl._connectors = out;
        };

        // ── Cross-panel navigation ─────────────────────────────────────────────
        //
        // scrollToField — scrolls the left panel to the given fieldId and flashes it
        ctrl.scrollToField = function (fieldId) {
            var srcPanel = document.querySelector('.cbew-mapping-sources');
            if (!srcPanel) { return; }
            var el = srcPanel.querySelector('[data-field-id="' + fieldId + '"]');
            // Fallback: normalize array indices so IntelliSense paths ($.a.b)
            // match tree DOM paths ($.a[0].b).
            if (!el) {
                var normId = _normId(fieldId);
                var all = srcPanel.querySelectorAll('[data-field-id]');
                for (var i = 0; i < all.length; i++) {
                    if (_normId(all[i].getAttribute('data-field-id')) === normId) {
                        el = all[i]; break;
                    }
                }
            }
            if (!el) { return; }
            el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            el.classList.add('cbew-map-field--flash');
            $timeout(function () { el.classList.remove('cbew-map-field--flash'); }, 1400);
        };

        // navigateToSource — clicking a mapped component with exactly 1 source.
        // Expands the source section if collapsed, then scrolls left panel to it.
        ctrl.navigateToSource = function (comp, $event) {
            if ($event) { $event.stopPropagation(); }
            var mappings = ctrl.getMappingsForComponent(comp);
            if (mappings.length !== 1) { return; }
            var src = mappings[0].source;
            if (src.opIdx !== undefined) {
                ctrl._sectionState['op_' + src.opIdx] = true;
                var suffix = null;
                if (src.sourceType === 'CONTEXT') {
                    suffix = (src.path && src.path.indexOf('$bm.response') === 0) ? '_res_ctx' : '_req_ctx';
                } else if (src.sourceType === 'REQUEST_FIELD') { suffix = '_req_payload'; }
                else if (src.sourceType === 'RESPONSE_FIELD') { suffix = '_res_payload'; }
                else if (src.sourceType === 'HEADER') { suffix = '_headers'; }
                else if (src.sourceType === 'CUSTOM_HEADER') { suffix = '_custom_hdrs'; }
                else if (src.sourceType === 'URI_PARAM') { suffix = '_uri_params'; }
                else if (src.sourceType === 'QUERY_PARAM') { suffix = '_query_params'; }
                if (suffix) { ctrl._sectionState['op_' + src.opIdx + suffix] = true; }
            }
            $timeout(function () { ctrl.scrollToField(src.id); }, 60);
        };

        // navigateToComponent — clicking the navigate button on a mapped source field.
        // Scrolls the right panel to the target component and flashes it.
        ctrl.navigateToComponent = function (fieldId, $event) {
            if ($event) { $event.stopPropagation(); }
            var m = ctrl.mappings.filter(function (x) { return x.source.id === fieldId; })[0];
            if (!m) { return; }
            var tgtPanel = document.querySelector('.cbew-map-components');
            if (!tgtPanel) { return; }
            var el = tgtPanel.querySelector('[data-comp-id="' + m.targetComponentId + '"]');
            if (!el) { return; }
            el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            el.classList.add('cbew-map-component--flash');
            $timeout(function () { el.classList.remove('cbew-map-component--flash'); }, 1400);
        };

        // openBmelEditor — opens the BMEL expression editor for a connector.
        // TODO: implement the actual editor dialog.
        ctrl.openBmelEditor = function (c, $event) {
            if ($event) { $event.stopPropagation(); }

            // Parsear el connector id: "sourceId|targetCompId"  o  "sourceId|col|targetCompId|colName"
            var parts = c.id.split('|');
            var isCol       = parts[1] === 'col';
            var sourceId    = parts[0];
            var targetCompId = isCol ? parts[2] : parts[1];
            var colName     = isCol ? parts[3] : null;

            // Obtener nombre del target component desde mappings existentes
            var existingMappings = ctrl.mappings.filter(function (m) {
                return String(m.targetComponentId) === String(targetCompId);
            });
            var targetCompName = existingMappings.length
                ? existingMappings[0].targetComponentName
                : String(targetCompId);

            // ── Sources disponibles ──────────────────────────────────────
            // Para un conector de columna (|col|) solo mostramos el source
            // específico de esa columna — no todos los sources del componente.
            // Para conectores normales mostramos todos los sources del componente.
            var allSources;
            if (isCol) {
                // Resolver el source de la columna usando la misma lógica que
                // navigateToMapping: buscar en m.source y en m.columnMappings.
                var normSrcId = _normId(sourceId);
                var colSrc = null;
                for (var mi = 0; mi < ctrl.mappings.length; mi++) {
                    var mapping = ctrl.mappings[mi];
                    if (mapping.source.id === sourceId) { colSrc = mapping.source; break; }
                    if (mapping.columnMappings) {
                        var colKeys = Object.keys(mapping.columnMappings);
                        for (var ci = 0; ci < colKeys.length; ci++) {
                            var cm = mapping.columnMappings[colKeys[ci]];
                            if (cm && cm.source &&
                                (cm.source.id === sourceId || _normId(cm.source.id) === normSrcId)) {
                                colSrc = cm.source; break;
                            }
                        }
                        if (colSrc) { break; }
                    }
                }
                allSources = colSrc ? _buildSourcesForBmel([colSrc], _isInvocationTable(targetCompId)) : [];
            } else {
                allSources = _buildAllSourcesForBmel(targetCompId);
            }

            // Expresión actual — con scope por columna cuando aplica
            var exprKey = isCol ? (targetCompId + '|col|' + colName) : targetCompId;
            var currentExpr = ctrl.targetExpressions[exprKey] || '';

            var _svgComp = ctrl.getFrameComponents().filter(function (fc) {
                return String(fc.frame_component_id || fc.id || '') === String(targetCompId);
            })[0] || null;
            var _eiSvg = _svgComp
                ? (isCol ? _getExpectedBmelInfoForCol({ attribute_type_id: null })
                         : _getExpectedBmelInfo(_svgComp))
                : { type: null, label: null };
            BmelEditorService.openEditor({
                resourceTypes:       _bmelResourceTypes(),
                targetComponentId:   targetCompId,
                targetComponentName: targetCompName + (colName ? ' › ' + colName : ''),
                currentExpression:   currentExpr,
                availableSources:    allSources,
                expectedBmelType:    _eiSvg.type,
                expectedTypeLabel:   _eiSvg.label,
                enumValues:          (_eiSvg.type === 'bmel:enum' && _svgComp)
                                         ? _getEnumValuesFromTemplate(_svgComp.attribute_type_id)
                                         : []
            }).then(function (expression) {
                ctrl.targetExpressions[exprKey] = expression;
                _validateAllExpressions();
                _showToast('\u2713 Expresi\u00f3n guardada para \'' + targetCompName + (colName ? ' \u203a ' + colName : '') + '\'');
                $timeout(ctrl.recalcConnectors, 50);
            });
        };


        // ── 4. _buildAllSourcesForBmel(targetCompId)
        //
        //  Solo incluye sources conectados al target component solicitado.
        //  Deduplica por source.id.
        //
        //  Tanto el .label (display en el panel) como el .ref (texto insertado
        //  en Monaco) usan el formato canónico:
        //      {Operacion:Seccion}.$.campo.anidado
        //
        //  Garantiza consistencia visual entre el panel de sources, los chips
        //  y la expresión que se escribe en el editor.

        // Construye la lista de sources para el BMEL editor a partir de un
        // array de source objects ya resueltos (usado para conectores de columna).
        // Si alguno de los sources es un array (e.g. $.accounts.recentTransactions
        // mapeado a una columna NESTED_TABLE), genera también sus hijos escalares
        // como sources virtuales column:<type> — idéntico comportamiento al que
        // ocurre cuando un array se conecta a un componente no-NESTED_TABLE.
        // _toBmelSource — one directive source → BMEL editor source entry.
        // Shared by _buildSourcesForBmel and _buildAllSourcesForBmel so both
        // build identical refs:
        //   ONE  operation:  {Op:Response Payload}.$.field
        //   MANY operation:  {Op:Response Payload[]}.$.field  — valueType becomes
        //                    column:<type> (array of the N occurrences), same as
        //                    an array's children, so aggregations type-check.
        //   whole MANY op:   {Op:Invocations[]}.$            — array of invocations
        //
        // rowScoped: the source feeds a column of a NESTED_TABLE whose rows ARE the
        // invocations, so per row it holds a single invocation's value — keep its
        // scalar type (same ref, the "[]" still marks it as a MANY-op source).
        function _toBmelSource(src, rowScoped) {
            var opIdx  = src.opIdx;
            var opName = '';
            if (opIdx !== undefined && ctrl.selectedOperations && ctrl.selectedOperations[opIdx]) {
                opName = _opDisplayName(ctrl.selectedOperations[opIdx], opIdx);
            }
            var sectionLabel = _sectionLabel(src, true);
            var bmelRef;
            if (src.sourceType === 'OPERATION') {
                bmelRef = '{' + opName + ':' + sectionLabel + '}.$';
            } else {
                var fieldPath = (src.bmelPath || src.path || '').replace(/\[\d+\]/g, '').replace(/^\$\.?/, '');
                bmelRef = '{' + (opName ? opName + ':' : '') + sectionLabel + '}.$.' + fieldPath;
            }

            var valueType = src.valueType || 'string';
            if (!rowScoped && _isManySource(src) && src.sourceType !== 'OPERATION'
                    && valueType !== 'array' && valueType.indexOf('column:') !== 0) {
                valueType = 'column:' + valueType;
            }
            // Row-scoped (one invocation) value inside an array of that invocation:
            // still one value per array element — a column (e.g. bmel:first(...)).
            if (rowScoped && _isInsideArray(src)
                    && valueType !== 'array' && valueType.indexOf('column:') !== 0) {
                valueType = 'column:' + valueType;
            }

            return {
                id:            src.id,
                label:         bmelRef,
                path:          src.path,
                sourceType:    src.sourceType,
                valueType:     valueType,
                opIdx:         opIdx,
                opName:        opName,
                ref:           bmelRef,
                isArrayColumn: src.isArrayColumn || false,
                // Array of the N invocations of a MANY operation (not a payload
                // array) — the validator only lets it reach array-typed params.
                isOccurrences: !rowScoped && _isManySource(src)
            };
        }

        function _buildSourcesForBmel(sources, rowScoped) {
            var seen   = {};
            var result = [];

            function _pushOne(src) {
                if (!src || !src.id || seen[src.id]) { return; }
                seen[src.id] = true;
                result.push(_toBmelSource(src, rowScoped));
                _arrayAncestorSources(src).forEach(function (arr) {     // see _buildAllSourcesForBmel
                    if (seen[arr.id]) { return; }
                    seen[arr.id] = true;
                    result.push(_toBmelSource(arr, rowScoped));
                });
            }

            sources.forEach(function (src) {
                _pushOne(src);

                // When the source itself is a (nested) array, generate virtual
                // column sources for its leaf children so the BMEL editor can
                // reference them with aggregation functions (AVG, MAX, MIN, …).
                // This mirrors the behaviour for non-NESTED_TABLE array sources
                // handled in _buildAllSourcesForBmel + _getArrayColumnSources.
                if (src && src.valueType === 'array') {
                    _getArrayColumnSources(src).forEach(function (colSrc) {
                        _pushOne(colSrc);
                    });
                }
            });
            return result;
        }

        // True when the NESTED_TABLE's rows are the invocations of a MANY operation.
        function _isInvocationTable(compId) {
            return ctrl.mappings.some(function (m) {
                return String(m.targetComponentId) === String(compId) && m.source.sourceType === 'OPERATION';
            });
        }

        function _buildAllSourcesForBmel(targetCompId) {
            var seen   = {};
            var result = [];

            function _pushSource(src, rowScoped) {
                if (!src || !src.id || seen[src.id]) { return; }
                seen[src.id] = true;
                result.push(_toBmelSource(src, rowScoped));
                // bmel:first(<the arrays it lies inside>).<field> references those arrays.
                _arrayAncestorSources(src).forEach(function (arr) {
                    if (seen[arr.id]) { return; }
                    seen[arr.id] = true;
                    result.push(_toBmelSource(arr, rowScoped));
                });
            }

            ctrl.mappings.forEach(function (m) {
                if (String(m.targetComponentId) !== String(targetCompId)) { return; }

                // Main source (array parent, scalar field, context var, etc.)
                _pushSource(m.source);

                // If the main source is an array on a non-NESTED_TABLE component,
                // generate virtual column sources from the tree cache so BMEL
                // aggregation functions have typed children to reference.
                // These are NOT stored in ctrl.mappings — they are ephemeral.
                if (m.source.valueType === 'array') {
                    var comp = null;
                    ctrl.getSortedComponents().forEach(function (c) {
                        if (String(c.frame_component_id || '') === String(targetCompId)) { comp = c; }
                    });
                    if (!comp || comp.frame_component_type !== 'NESTED_TABLE') {
                        _getArrayColumnSources(m.source).forEach(function (colSrc) {
                            _pushSource(colSrc);
                        });
                    }
                }

                // Column mappings (from nested tables or IntelliSense)
                if (m.columnMappings) {
                    var rowScoped = m.source.sourceType === 'OPERATION';
                    Object.keys(m.columnMappings).forEach(function (colName) {
                        var cm = m.columnMappings[colName];
                        if (!cm || !cm.source) { return; }
                        _pushSource(cm.source, rowScoped);

                        // A column's own source can itself be an array nested one
                        // level deeper than the table's row array (e.g. a NESTED_TABLE
                        // over `accounts[]` with a column aggregating `accounts[].
                        // recentTransactions[].amount` — recentTransactions is the
                        // column source here). Without this, aggregation expressions
                        // that drill into such a column's own children (.amount,
                        // .fraudScore, etc.) have no matching source and always fail
                        // validation, even though the expression is valid — mirrors
                        // the same expansion already done above for the row array.
                        if (cm.source.valueType === 'array') {
                            _getArrayColumnSources(cm.source).forEach(function (colSrc) {
                                _pushSource(colSrc);
                            });
                        }
                    });
                }
            });

            return result;
        }



        // _getExpectedBmelInfo — derive expectedBmelType + human-readable label from
        // a frame component object, using getFieldReferenceType / frame_component_type.
        function _getExpectedBmelInfo(comp) {
            var compType   = (comp.frame_component_type || comp.field_type || comp.type || 'CUSTOM').toUpperCase();
            var scalarType = ctrl.getFieldReferenceType(comp) || comp.scalar_attribute_type || null;
            // An OBJECT_FIELD_REF is mapped from the object's id too: the attribute it points at is
            // only read at runtime, so its type is not the type of the mapping.
            if (compType === 'OBJECT_FIELD_REF') {
                scalarType = 'STRING';
            }
            // An OBJECT_REFERENCE holds the referenced object's id — a single
            // STRING, same as an OBJECT_REF column (_getExpectedBmelInfoForCol).
            // It carries no attribute type, so without this it fell through to
            // bmel:any and skipped type checking — e.g. an array source (a MANY
            // operation's field) was accepted with no error.
            if (!scalarType && (compType === 'OBJECT_REFERENCE' || compType === 'OBJECT_REF')) {
                scalarType = 'STRING';
            }
            var bmelType   = BmelEditorService.frameTypeToExpectedBmel(compType, scalarType);
            var typeLabel  = BmelEditorService.bmelTypeLabel(bmelType)
                             + (scalarType ? ' (' + scalarType + ')'
                                : (compType !== 'CUSTOM' ? ' (' + compType + ')' : ''));
            return { type: bmelType, label: typeLabel };
        }

        // _getExpectedBmelInfoForCol — same for a NESTED_TABLE column.
        function _getExpectedBmelInfoForCol(col) {
            // OBJECT_REF and OBJECT_FIELD_REF columns are both mapped from the object's id (a STRING)
            if (col.column_type === 'OBJECT_REF' || col.column_type === 'OBJECT_FIELD_REF') {
                var referenceType = BmelEditorService.frameTypeToExpectedBmel('OBJECT_REFERENCE', 'STRING');
                return { type: referenceType,
                         label: BmelEditorService.bmelTypeLabel(referenceType) + ' (' + col.column_type + ')' };
            }
            var scalarType = ctrl.getColReferenceType(col) || col.scalar_attribute_type || null;
            var bmelType   = BmelEditorService.frameTypeToExpectedBmel('FIELD_REFERENCE', scalarType);
            var typeLabel  = BmelEditorService.bmelTypeLabel(bmelType)
                             + (scalarType ? ' (' + scalarType + ')' : '');
            return { type: bmelType, label: typeLabel };
        }

        // openBmelEditorForComponent — click directly on a frame component to open
        // the BMEL editor. Equivalent to clicking the ƒ button on a connector.
        // Does nothing if the component has no mappings or if the click is the
        // tail of a drag-and-drop connection gesture (_justConnected guard).
        ctrl.openBmelEditorForComponent = function (comp, $event) {
            if ($event) { $event.stopPropagation(); }
            // Ignore clicks that are the remate of a drag-drop connection
            if (ctrl._justConnected) { return; }
            var compId = String(comp.frame_component_id || comp.id || '');
            if (!compId) { return; }
            var sources = _buildAllSourcesForBmel(compId);
            if (!sources.length) { return; }   // nothing mapped — no editor
            var compName = ctrl.getComponentName(comp);
            var currentExpr = ctrl.targetExpressions[compId] || '';
            var _ei1 = _getExpectedBmelInfo(comp);
            BmelEditorService.openEditor({
                resourceTypes:       _bmelResourceTypes(),
                targetComponentId:   compId,
                targetComponentName: compName,
                currentExpression:   currentExpr,
                availableSources:    sources,
                expectedBmelType:    _ei1.type,
                expectedTypeLabel:   _ei1.label,
                enumValues:          _ei1.type === 'bmel:enum'
                                         ? _getEnumValuesFromTemplate(comp.attribute_type_id)
                                         : []
            }).then(function (expression) {
                ctrl.targetExpressions[compId] = expression;
                _validateAllExpressions();
                _showToast('\u2713 Expresi\u00f3n guardada para \'' + compName + '\'');
                $timeout(ctrl.recalcConnectors, 50);
            });
        };

        // openBmelEditorForColumn — click directly on a nested table column to open
        // the BMEL editor. Mirrors openBmelEditorForComponent but scoped to a
        // specific column slot. Only opens when the column has a mapped source.
        ctrl.openBmelEditorForColumn = function (comp, col, $event) {
            if ($event) { $event.stopPropagation(); }
            if (ctrl._justConnected) { return; }

            var compId  = String(comp.frame_component_id || '');
            var colName = col.column_name;
            if (!compId || !colName) { return; }

            // Locate the column mapping source
            var colSrc = null;
            for (var i = 0; i < ctrl.mappings.length; i++) {
                var m = ctrl.mappings[i];
                if (String(m.targetComponentId) === compId && m.columnMappings && m.columnMappings[colName]) {
                    colSrc = m.columnMappings[colName].source;
                    break;
                }
            }
            if (!colSrc) { return; }   // unmapped column — nothing to edit

            var sources = _buildSourcesForBmel([colSrc], _isInvocationTable(compId));
            if (!sources.length) { return; }

            var exprKey     = compId + '|col|' + colName;
            var currentExpr = ctrl.targetExpressions[exprKey] || '';
            var compName    = ctrl.getComponentName(comp);
            var title       = compName + ' \u203a ' + colName;

            var _eiCol = _getExpectedBmelInfoForCol(col);
            BmelEditorService.openEditor({
                resourceTypes:       _bmelResourceTypes(),
                targetComponentId:   compId,
                targetComponentName: title,
                currentExpression:   currentExpr,
                availableSources:    sources,
                expectedBmelType:    _eiCol.type,
                expectedTypeLabel:   _eiCol.label,
                enumValues:          _eiCol.type === 'bmel:enum'
                                         ? (col.attribute_type_id
                                             ? _getEnumValuesFromTemplate(col.attribute_type_id)
                                             : (col.enum_values || []))   // SCALAR_INLINE: its own values
                                         : []
            }).then(function (expression) {
                ctrl.targetExpressions[exprKey] = expression;
                _validateAllExpressions();
                _showToast('\u2713 Expresi\u00f3n guardada para \'' + title + '\'');
                $timeout(ctrl.recalcConnectors, 50);
            });
        };

        // _expandToPath — uncollapse all ancestor tree nodes for a given field path
        // so the leaf is visible in the DOM before scrollToField tries to find it.
        function _expandToPath(opIdx, side, targetPath) {
            var key  = opIdx + '_' + side;
            var tree = ctrl._treeCache[key];
            if (!tree) { return; }
            var normTarget = _normId(targetPath);

            function _walk(nodes) {
                nodes.forEach(function (node) {
                    var normNode = _normId(node.path);
                    // Ancestor: our path is a strict prefix of the target
                    if (normTarget.indexOf(normNode + '.') === 0 || normTarget.indexOf(normNode + '[') === 0) {
                        node.collapsed = false;
                        if (node._children && node._children.length) { _walk(node._children); }
                    }
                });
            }

            _walk(tree);
            // Invalidate flatten cache so ng-repeat re-renders with expanded nodes
            delete ctrl._flattenCache[key];
        }

        // navigateToMapping — click on a connector arrow.
        // Expands and scrolls both panels so source and target are simultaneously visible.
        ctrl.navigateToMapping = function (c, $event) {
            if ($event) { $event.stopPropagation(); }
            // Connector id format: "sourceId|targetCompId"  or  "sourceId|col|targetCompId|colName"
            var parts = c.id.split('|');
            var isCol = parts[1] === 'col';
            var sourceFieldId = parts[0];
            var targetCompId  = isCol ? parts[2] : parts[1];
            var normSrcId     = _normId(sourceFieldId);

            // ── Resolve source: check m.source first, then m.columnMappings ──
            // Column connectors (|col|) come from IntelliSense and store their
            // source inside m.columnMappings, not as a top-level m.source entry.
            var src = null;
            for (var mi = 0; mi < ctrl.mappings.length; mi++) {
                var mapping = ctrl.mappings[mi];
                if (mapping.source.id === sourceFieldId) { src = mapping.source; break; }
                if (isCol && mapping.columnMappings) {
                    var colKeys = Object.keys(mapping.columnMappings);
                    for (var ci = 0; ci < colKeys.length; ci++) {
                        var cm = mapping.columnMappings[colKeys[ci]];
                        if (cm && cm.source &&
                            (cm.source.id === sourceFieldId || _normId(cm.source.id) === normSrcId)) {
                            src = cm.source; break;
                        }
                    }
                    if (src) { break; }
                }
            }

            // ── Left panel: expand section + parent tree nodes, scroll ─────
            if (src) {
                if (src.opIdx !== undefined) {
                    ctrl._sectionState['op_' + src.opIdx] = true;
                    var suffix = null;
                    if (src.sourceType === 'CONTEXT') {
                        suffix = (src.path && src.path.indexOf('$bm.response') === 0) ? '_res_ctx' : '_req_ctx';
                    } else if (src.sourceType === 'REQUEST_FIELD') { suffix = '_req_payload'; }
                    else if (src.sourceType === 'RESPONSE_FIELD') { suffix = '_res_payload'; }
                    else if (src.sourceType === 'HEADER') { suffix = '_headers'; }
                    else if (src.sourceType === 'CUSTOM_HEADER') { suffix = '_custom_hdrs'; }
                    else if (src.sourceType === 'URI_PARAM') { suffix = '_uri_params'; }
                    else if (src.sourceType === 'QUERY_PARAM') { suffix = '_query_params'; }
                    if (suffix) { ctrl._sectionState['op_' + src.opIdx + suffix] = true; }

                    // Expand parent tree nodes so the child leaf is in the DOM
                    if (src.sourceType === 'REQUEST_FIELD' || src.sourceType === 'RESPONSE_FIELD') {
                        var treeSide = src.sourceType === 'REQUEST_FIELD' ? 'request' : 'response';
                        _expandToPath(src.opIdx, treeSide, src.path);
                    }
                }
                // Longer timeout: Angular needs a digest to re-render expanded nodes
                var srcIdToScroll = src.id;
                $timeout(function () { ctrl.scrollToField(srcIdToScroll); }, 80);
            }

            // ── Right panel: scroll to target ─────────────────────────────
            var tgtPanel = document.querySelector('.cbew-map-components');
            if (tgtPanel) {
                if (isCol) {
                    // Column connector — scroll to the specific column slot inside
                    // the NESTED_TABLE, not to the table header row.
                    // First make sure the nested table is expanded.
                    var colName = parts[3];
                    ctrl._nestedTableOpen[targetCompId] = true;
                    $timeout(function () {
                        var colEl = tgtPanel.querySelector(
                            '[data-col-comp="' + targetCompId + '"][data-col-name="' + colName + '"]'
                        );
                        if (colEl) {
                            colEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                            colEl.classList.add('cbew-ntbl-col--flash');
                            $timeout(function () { colEl.classList.remove('cbew-ntbl-col--flash'); }, 1400);
                        }
                    }, 40);
                } else {
                    var compEl = tgtPanel.querySelector('[data-comp-id="' + targetCompId + '"]');
                    if (compEl) {
                        compEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                        compEl.classList.add('cbew-map-component--flash');
                        $timeout(function () { compEl.classList.remove('cbew-map-component--flash'); }, 1400);
                    }
                }
            }
        };


        // ── _reconcileCardinality ──────────────────────────────────────────
        //
        // Cardinality is chosen in the operations step, so it can change after
        // mappings and expressions already exist (Back → toggle One/Many → Next,
        // or a version persisted under the other cardinality). Brings both in
        // line with the current cardinality of every operation:
        //   • drops mappings that are impossible now — the whole-operation source
        //     of an operation that went back to ONE, and a NESTED_TABLE fed by a
        //     payload array of an operation that is now MANY (nested array);
        //   • rewrites BMEL refs of each operation to its section notation —
        //     {Op:Section[]} for MANY, {Op:Section} for ONE. Also normalises refs
        //     coming from IntelliSense, which is unaware of cardinality.
        // Only reassigns state that actually changed, so an untouched preload
        // stays a preload for the edit wizard's dirty-check.
        function _reconcileCardinality() {
            var ops = ctrl.selectedOperations || [];
            if (!ops.length) { return; }

            var compTypes = {};
            ctrl.getSortedComponents().forEach(function (c) {
                compTypes[String(c.frame_component_id || c.id || '')] = c.frame_component_type;
            });

            var dropped = [];
            var kept = (ctrl.mappings || []).filter(function (m) {
                var entry = _opEntryOf(m.source);
                if (!entry) { return true; }
                var many = ctrl.isManyOp(entry);
                var impossible = (m.source.sourceType === 'OPERATION' && !many)
                    || (many && m.source.sourceType !== 'OPERATION'
                        && compTypes[String(m.targetComponentId)] === 'NESTED_TABLE');
                if (impossible) { dropped.push(m); }
                return !impossible;
            });
            if (dropped.length) {
                ctrl.mappings = kept;
                dropped.forEach(function (m) {
                    var stillHasSources = kept.some(function (k) { return k.targetComponentId === m.targetComponentId; });
                    if (!stillHasSources) { _clearExpressionsForComp(m.targetComponentId); }
                });
                _showToast('⚠️ ' + dropped.length + ' mapping(s) removed — no longer valid for the operation cardinality.');
            }

            var exprs = ctrl.targetExpressions || {};
            ops.forEach(function (entry, opIdx) {
                var name = _opDisplayName(entry, opIdx);
                var suffix = ctrl.isManyOp(entry) ? '[]' : '';
                var re = new RegExp('\\{' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ':([^}]*?)\\s*(?:\\[\\])?\\s*\\}', 'g');
                Object.keys(exprs).forEach(function (key) {
                    var expr = exprs[key];
                    if (!expr) { return; }
                    var next = expr.replace(re, function (all, section) {
                        return '{' + name + ':' + section.trim() + suffix + '}';
                    });
                    if (next !== expr) { exprs[key] = next; }
                });
            });
        }

        $scope.$watch('ctrl.preloadMappings', function (preload) {
            if (!preload || !preload.length) { return; }
            // Only apply the preload if there are no existing mappings.
            // When the user navigates Back and returns to this step, the
            // directive is recreated and this watch fires again — without
            // this guard it would overwrite any manual edits made since
            // IntelliSense ran.
            if (ctrl.mappings && ctrl.mappings.length > 0) { return; }
            var translated = _translateIntelliSenseMappings(preload);
            if (translated.length > 0) {
                // Tag the array itself as a preload write so the wizard's dirty-check
                // (EditBizEventVersionController) can recognize it unambiguously —
                // it must NOT infer this from the array going 0 → N in length, since
                // a user's first real mapping on an originally-empty step produces the
                // exact same transition and would otherwise be silently ignored.
                translated._isPreloadWrite = true;
                ctrl.mappings = translated;

                // Aplicar expresiones BMEL de IntelliSense al modelo interno
                if (!ctrl.targetExpressions) { ctrl.targetExpressions = {}; }
                translated.forEach(function (m) {
                    // Expresión del componente (reglas a-d)
                    if (m.bmelExpression && !ctrl.targetExpressions[m.targetComponentId]) {
                        ctrl.targetExpressions[m.targetComponentId] = m.bmelExpression;
                    }
                    // Expresiones de columnas (regla e — hijos de 2do nivel)
                    if (m.columnMappings) {
                        Object.keys(m.columnMappings).forEach(function (colName) {
                            var cmEntry = m.columnMappings[colName];
                            if (cmEntry && cmEntry.bmelExpression) {
                                var exprKey = m.targetComponentId + '|col|' + colName;
                                if (!ctrl.targetExpressions[exprKey]) {
                                    ctrl.targetExpressions[exprKey] = cmEntry.bmelExpression;
                                }
                            }
                        });
                    }
                });

                // Align with the operations' current cardinality (it may have been
                // toggled since this version was saved) before validating.
                _reconcileCardinality();

                // Validate so pre-existing invalid expressions (e.g. broken demo/
                // legacy data) show their error triangle — and block Apply Changes —
                // from the moment the mapper renders, instead of only after the
                // user's first edit happens to call _validateAllExpressions() as a
                // side effect.
                //
                // Wait on the BMEL function registry explicitly rather than a fixed
                // timeout: on a cold page load (fresh refresh, competing auth/config/
                // geolocation requests) BmelEditorService.loadFunctions() — kicked off
                // by the wizard a couple steps earlier — can easily take longer than
                // any fixed delay we'd pick, which left KNOWN_FN_NAMES empty at
                // validation time and flagged every function-based expression as
                // invalid until the user's first edit re-validated against the (by
                // then) populated registry. If functions are already loaded (the
                // common case — same singleton service, warm from an earlier visit
                // this session) this resolves immediately. The extra 80ms only
                // covers the other lazily-built caches (_buildAllSourcesForBmel's
                // tree cache, getSortedComponents' frame-components cache).
                Promise.all([
                    BmelEditorService.loadMonaco(),
                    BmelEditorService.getFunctions().length
                        ? Promise.resolve()
                        : BmelEditorService.loadFunctions()
                ]).then(function () {
                    BmelEditorService.refreshLanguageTokenizer();
                }).catch(angular.noop).then(function () {
                    $timeout(function () {
                        _validateAllExpressions();
                        ctrl.recalcConnectors();
                    }, 80);
                });
            }
        });

        // Watch mappings → redraw permanent connectors
        $scope.$watchCollection('ctrl.mappings', function () {
            $timeout(ctrl.recalcConnectors, 50);
        });

        // Sticky headers have a variable height (names wrap): expose it on their
        // parent for the CSS, and keep it current as they resize —
        //   • an operation header → --cbew-op-header-h, so its section labels stick right under it
        //   • an expanded table's component row → --cbew-comp-header-h, so a column
        //     scrolled into view isn't left under it
        var _opHeaderObserver = window.ResizeObserver ? new ResizeObserver(function (entries) {
            entries.forEach(function (e) {
                var parent = e.target.parentElement;
                if (!parent) { return; }
                var prop = e.target.classList.contains('cbew-map-component') ? '--cbew-comp-header-h' : '--cbew-op-header-h';
                parent.style.setProperty(prop, e.target.offsetHeight + 'px');
            });
        }) : null;
        function _observeStickyHeaders() {
            if (!_opHeaderObserver) { return; }
            _opHeaderObserver.disconnect();
            var srcPanel = document.querySelector('.cbew-mapping-sources');
            if (srcPanel) {
                srcPanel.querySelectorAll('.cbew-map-section__header').forEach(function (h) {
                    _opHeaderObserver.observe(h);
                });
            }
            var tgtPanel = document.querySelector('.cbew-map-components');
            if (tgtPanel) {
                tgtPanel.querySelectorAll('.cbew-map-component--sticky').forEach(function (h) {
                    _opHeaderObserver.observe(h);
                });
            }
        }
        $scope.$watchCollection('ctrl.selectedOperations', function () {
            $timeout(_observeStickyHeaders, 0);
        });
        // Tables are expanded by the toggle, on connecting their array, or on navigating to a column
        $scope.$watch(function () {
            return Object.keys(ctrl._nestedTableOpen || {}).filter(function (k) {
                return ctrl._nestedTableOpen[k];
            }).join('|');
        }, function () {
            $timeout(_observeStickyHeaders, 0);
        });

        // Cleanup on directive destroy
        $scope.$on('$destroy', function () {
            document.removeEventListener('mousemove', _onConnectMove);
            document.removeEventListener('mouseup', _onConnectUp);
            if (ctrl._srcScrollOff) ctrl._srcScrollOff();
            if (ctrl._tgtScrollOff) ctrl._tgtScrollOff();
            if (_opHeaderObserver) _opHeaderObserver.disconnect();
        });

        // ── Initialisation ───────────────────────────────────────────────
        // Using $onInit ensures one-way bindings (selectedOperations,
        // selectedFrameType) are already populated before we access them.
        ctrl.$onInit = function () {
            ctrl.selectedSourceField = null;
            ctrl._treeCache = {};
            ctrl._flattenCache = {};
            ctrl._simpleSourcesCache = {};
            ctrl._fieldRefTypeCache = {};   // reset on each directive init
            ctrl._nestedTableOpen = {};
            ctrl._sortedCompsCache = null;
            ctrl._sortedCompsFtId = null;
            ctrl._frameCompsId = null;
            ctrl._frameCompsCache = null;
            ctrl._sectionState = {};
            ctrl._contextVarsCache = {};
            ctrl.connecting = null;
            ctrl.connectingTarget = null;

            ctrl.intelliSenseRunning = false;

            // targetExpressions is two-way bound to the wizard controller so it
            // survives directive destroy/recreate when the user navigates back.
            ctrl.targetExpressions = ctrl.targetExpressions || {};

            ctrl._connectors = [];
            (ctrl.selectedOperations || []).forEach(function (entry, opIdx) {
                var reqSys = _buildCtxVars(opIdx, 'req_sys');
                if (!_getRequestSample(entry.op)) {
                    reqSys = reqSys.filter(function (v) { return v.id.indexOf('CV_REQ_SIZE') === -1; });
                }
                ctrl._contextVarsCache[opIdx + '_req_sys'] = reqSys;

                ctrl._contextVarsCache[opIdx + '_req_token'] = _buildCtxVars(opIdx, 'req_token');

                var resSys = _buildCtxVars(opIdx, 'res_sys');
                if (!_getOpResponseSample(entry.op)) {
                    resSys = resSys.filter(function (v) { return v.id.indexOf('CV_RES_SIZE') === -1; });
                }
                ctrl._contextVarsCache[opIdx + '_res_sys'] = resSys;
            });
            // Scroll listeners — recalculate connector positions when panels scroll
            $timeout(function () {
                var srcPanel = document.querySelector('.cbew-mapping-sources');
                var tgtPanel = document.querySelector('.cbew-map-components'); /* cbew-mapping-target tiene overflow:hidden — el scroll real es cbew-map-components */
                var onScroll = function () { ctrl.recalcConnectors(); $scope.$applyAsync(); };
                if (srcPanel) {
                    srcPanel.addEventListener('scroll', onScroll);
                    ctrl._srcScrollOff = function () { srcPanel.removeEventListener('scroll', onScroll); };
                }
                if (tgtPanel) {
                    tgtPanel.addEventListener('scroll', onScroll);
                    ctrl._tgtScrollOff = function () { tgtPanel.removeEventListener('scroll', onScroll); };
                }
            }, 200);

            // Cardinality may have been toggled in the operations step since the
            // mappings bound from the wizard were drawn (Back → change → Next).
            if (ctrl.mappings && ctrl.mappings.length) {
                _reconcileCardinality();
            }

            // Rebuild _bmelErrorMap from the expressions that survived via the
            // two-way binding on targetExpressions. Without this, error icons
            // disappear after back/forward navigation even though bmelErrors
            // (also bound) correctly reflects the global error state.
            if (ctrl.targetExpressions && Object.keys(ctrl.targetExpressions).length) {
                _validateAllExpressions();
            }
        };
    });